import { isSExpr, isBinding, getOperator, getArgs, isTensorValue, describeTensorMismatch, contractFieldName, validateContract, isFieldValue, isEventPayloadValue, isSessionHistoryEntry, isPlanSnapshot, behaviorRef, BehaviorValueSchema, JsonValueSchema, applyBehaviorOverrides, instantiateQuasiquote, toProgramData, decodeQuoteBody, tensorLastDimSize, mapTensorLastDim, OrbitalZodSchema, safeParseOrbitalSchema } from '@almadar/core';
export { CORE_BINDINGS, ExpressionSchema, SExprSchema, collectBindings, getArgs, getOperator, isBinding, isSExpr, isSExprAtom, isSExprCall, isValidBinding, parseBinding, sexpr, walkSExpr } from '@almadar/core';
import { assertOperatorArity } from '@almadar/std/registry';

// types/expression.ts

// context.ts
function createMinimalContext(entity = {}, payload = {}, state = "initial") {
  return {
    entity,
    payload,
    state,
    now: Date.now(),
    singletons: /* @__PURE__ */ new Map()
  };
}
function createEffectContext(base, handlers) {
  return {
    ...base,
    ...handlers
  };
}
function createChildContext(parent, locals) {
  const mergedLocals = new Map(parent.locals);
  locals.forEach((value, key2) => mergedLocals.set(key2, value));
  return {
    ...parent,
    locals: mergedLocals
  };
}
var CLIENT_ONLY_BINDING_ROOTS = /* @__PURE__ */ new Set(["trait"]);
var PARSED_PATH_CACHE = /* @__PURE__ */ new Map();
function parseBindingPath(withoutPrefix) {
  const cached = PARSED_PATH_CACHE.get(withoutPrefix);
  if (cached) return cached;
  const parts = withoutPrefix.split(".").flatMap((seg) => {
    const m = seg.match(/^([\w]+)((?:\[\d+\])+)$/);
    if (!m) return [seg];
    const head = m[1];
    const indices = Array.from(m[2].matchAll(/\[(\d+)\]/g)).map((x) => x[1]);
    return [head, ...indices];
  });
  const parsed = { root: parts[0], path: parts.slice(1) };
  PARSED_PATH_CACHE.set(withoutPrefix, parsed);
  return parsed;
}
function resolveBinding(binding, ctx) {
  if (!binding.startsWith("@")) {
    return void 0;
  }
  const { root, path } = parseBindingPath(binding.slice(1));
  if (CLIENT_ONLY_BINDING_ROOTS.has(root)) {
    return binding;
  }
  let value;
  if (ctx.locals?.has(root)) {
    value = ctx.locals.get(root);
  } else {
    switch (root) {
      case "entity":
        value = ctx.entity;
        break;
      case "payload":
        value = ctx.payload;
        break;
      case "callsitePayload":
        value = ctx.callsitePayload;
        break;
      case "state":
        return ctx.state;
      // @state has no path
      case "now":
        return ctx.now;
      // @now has no path
      case "user":
        value = ctx.user;
        break;
      case "config":
        value = ctx.config;
        break;
      case "pages":
        return ctx.pages;
      case "currentTheme":
        return ctx.currentTheme;
      case "locale":
        return ctx.locale;
      case "event":
        value = ctx.event;
        break;
      case "prevEvents":
        return ctx.prevEvents ?? [];
      case "prevStates":
        return ctx.prevStates ?? [];
      case "fromState":
        return ctx.fromState;
      case "toState":
        return ctx.toState;
      default:
        value = ctx.singletons.get(root);
        break;
    }
  }
  for (let i = 0; i < path.length; i++) {
    const segment = path[i];
    if (value === null || value === void 0) {
      if (ctx.strictBindings) {
        const resolvedSoFar = [root, ...path.slice(0, i)].join(".");
        console.warn(
          `[Binding] @${root}.${path.join(".")} resolved to undefined (failed at "${segment}" \u2014 @${resolvedSoFar} is ${value === null ? "null" : "undefined"})`
        );
      }
      return void 0;
    }
    if (typeof value === "object") {
      value = value[segment];
    } else {
      if (ctx.strictBindings) {
        const resolvedSoFar = [root, ...path.slice(0, i)].join(".");
        console.warn(
          `[Binding] @${root}.${path.join(".")} resolved to undefined (cannot navigate "${segment}" on non-object at @${resolvedSoFar})`
        );
      }
      return void 0;
    }
  }
  if (value === void 0 && path.length > 0 && ctx.strictBindings) {
    console.warn(
      `[Binding] @${root}.${path.join(".")} resolved to undefined`
    );
  }
  if (root === "config" && !ctx.locals?.has(root) && typeof value === "string" && /^@[^\s]+$/.test(value) && !CLIENT_ONLY_BINDING_ROOTS.has(parseBindingPath(value.slice(1)).root) && !inFlightConfigBindings.has(binding)) {
    inFlightConfigBindings.add(binding);
    try {
      return resolveBinding(value, ctx);
    } finally {
      inFlightConfigBindings.delete(binding);
    }
  }
  return value;
}
var inFlightConfigBindings = /* @__PURE__ */ new Set();

// operators/arithmetic.ts
function evalAdd(args, evaluate2, ctx) {
  return args.reduce((sum, arg) => sum + toNumber(evaluate2(arg, ctx)), 0);
}
function evalSubtract(args, evaluate2, ctx) {
  if (args.length === 1) {
    return -toNumber(evaluate2(args[0], ctx));
  }
  return toNumber(evaluate2(args[0], ctx)) - toNumber(evaluate2(args[1], ctx));
}
function evalMultiply(args, evaluate2, ctx) {
  return args.reduce((product, arg) => product * toNumber(evaluate2(arg, ctx)), 1);
}
function evalDivide(args, evaluate2, ctx) {
  const dividend = toNumber(evaluate2(args[0], ctx));
  const divisor = toNumber(evaluate2(args[1], ctx));
  if (divisor === 0) {
    return dividend >= 0 ? Infinity : -Infinity;
  }
  return dividend / divisor;
}
function evalModulo(args, evaluate2, ctx) {
  return toNumber(evaluate2(args[0], ctx)) % toNumber(evaluate2(args[1], ctx));
}
function evalAbs(args, evaluate2, ctx) {
  return Math.abs(toNumber(evaluate2(args[0], ctx)));
}
function evalMin(args, evaluate2, ctx) {
  const values = args.map((arg) => toNumber(evaluate2(arg, ctx)));
  return Math.min(...values);
}
function evalMax(args, evaluate2, ctx) {
  const values = args.map((arg) => toNumber(evaluate2(arg, ctx)));
  return Math.max(...values);
}
function evalFloor(args, evaluate2, ctx) {
  return Math.floor(toNumber(evaluate2(args[0], ctx)));
}
function evalCeil(args, evaluate2, ctx) {
  return Math.ceil(toNumber(evaluate2(args[0], ctx)));
}
function evalRound(args, evaluate2, ctx) {
  return Math.round(toNumber(evaluate2(args[0], ctx)));
}
function evalClamp(args, evaluate2, ctx) {
  const value = toNumber(evaluate2(args[0], ctx));
  const min = toNumber(evaluate2(args[1], ctx));
  const max = toNumber(evaluate2(args[2], ctx));
  return Math.max(min, Math.min(max, value));
}
function toNumber(value) {
  if (typeof value === "number") return value;
  if (typeof value === "string") {
    const parsed = parseFloat(value);
    return isNaN(parsed) ? 0 : parsed;
  }
  if (typeof value === "boolean") return value ? 1 : 0;
  return 0;
}

// operators/comparison.ts
function evalEqual(args, evaluate2, ctx) {
  const left = evaluate2(args[0], ctx);
  const right = evaluate2(args[1], ctx);
  return deepEqual(left, right);
}
function evalNotEqual(args, evaluate2, ctx) {
  const left = evaluate2(args[0], ctx);
  const right = evaluate2(args[1], ctx);
  return !deepEqual(left, right);
}
function evalLessThan(args, evaluate2, ctx) {
  const left = evaluate2(args[0], ctx);
  const right = evaluate2(args[1], ctx);
  return toComparable(left) < toComparable(right);
}
function evalGreaterThan(args, evaluate2, ctx) {
  const left = evaluate2(args[0], ctx);
  const right = evaluate2(args[1], ctx);
  return toComparable(left) > toComparable(right);
}
function evalLessThanOrEqual(args, evaluate2, ctx) {
  const left = evaluate2(args[0], ctx);
  const right = evaluate2(args[1], ctx);
  return toComparable(left) <= toComparable(right);
}
function evalGreaterThanOrEqual(args, evaluate2, ctx) {
  const left = evaluate2(args[0], ctx);
  const right = evaluate2(args[1], ctx);
  return toComparable(left) >= toComparable(right);
}
function evalMatches(args, evaluate2, ctx) {
  const subject = evaluate2(args[0], ctx);
  const pattern = evaluate2(args[1], ctx);
  if (typeof subject !== "string" || typeof pattern !== "string") {
    return false;
  }
  try {
    const regex = new RegExp(pattern);
    return regex.test(subject);
  } catch {
    return false;
  }
}
function deepEqual(a, b) {
  if (a === b) return true;
  const aNull = a === null || a === void 0;
  const bNull = b === null || b === void 0;
  if (aNull || bNull) return aNull && bNull;
  if (typeof a !== typeof b) return false;
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    return a.every((val, idx) => deepEqual(val, b[idx]));
  }
  if (typeof a === "object" && typeof b === "object") {
    const keysA = Object.keys(a);
    const keysB = Object.keys(b);
    if (keysA.length !== keysB.length) return false;
    return keysA.every(
      (key2) => deepEqual(a[key2], b[key2])
    );
  }
  return false;
}
function toComparable(value) {
  if (typeof value === "number") return value;
  if (typeof value === "string") return value;
  if (typeof value === "boolean") return value ? 1 : 0;
  if (value === null || value === void 0) return 0;
  return String(value);
}

// operators/logic.ts
function evalAnd(args, evaluate2, ctx) {
  let last = true;
  for (const arg of args) {
    last = evaluate2(arg, ctx);
    if (!toBoolean(last)) {
      return last;
    }
  }
  return last;
}
function evalOr(args, evaluate2, ctx) {
  let last = false;
  for (const arg of args) {
    last = evaluate2(arg, ctx);
    if (toBoolean(last)) {
      return last;
    }
  }
  return last;
}
function evalNot(args, evaluate2, ctx) {
  return !toBoolean(evaluate2(args[0], ctx));
}
function evalIf(args, evaluate2, ctx) {
  const condition = toBoolean(evaluate2(args[0], ctx));
  if (condition) {
    return evaluate2(args[1], ctx);
  }
  return evaluate2(args[2], ctx);
}
function toBoolean(value) {
  return Boolean(value);
}

// operators/control.ts
function evalLet(args, evaluate2, ctx) {
  const rawBindings = args[0];
  const body = args[1];
  const bindingPairs = Array.isArray(rawBindings) ? rawBindings.map((b) => [b[0], b[1]]) : Object.entries(rawBindings);
  const locals = /* @__PURE__ */ new Map();
  let childCtx = ctx;
  for (const [name, valueExpr] of bindingPairs) {
    const value = evaluate2(valueExpr, childCtx);
    locals.set(name, value);
    childCtx = createChildContext(ctx, locals);
  }
  return evaluate2(body, childCtx);
}
function evalDo(args, evaluate2, ctx) {
  let result = void 0;
  for (const expr of args) {
    result = evaluate2(expr, ctx);
  }
  return result;
}
function evalWhen(args, evaluate2, ctx) {
  const condition = evaluate2(args[0], ctx);
  if (Boolean(condition)) {
    evaluate2(args[1], ctx);
  }
  return void 0;
}
var lambdaSources = /* @__PURE__ */ new WeakMap();
function lambdaSourceOf(value) {
  return typeof value === "function" ? lambdaSources.get(value) : void 0;
}
function evalFn(args, _evaluate, _ctx) {
  const params = args[0];
  const body = args[1];
  const closure = (item, evaluate2, ctx) => {
    const locals = /* @__PURE__ */ new Map();
    if (typeof params === "string") {
      locals.set(params, item);
    } else if (Array.isArray(params)) {
      const itemArray = Array.isArray(item) ? item : [item];
      for (let i = 0; i < params.length; i++) {
        locals.set(params[i], itemArray[i]);
      }
    }
    const childCtx = createChildContext(ctx, locals);
    return evaluate2(body, childCtx);
  };
  lambdaSources.set(closure, ["fn", params, body]);
  return closure;
}

// operators/collections.ts
function withItem(expr, evaluate2, ctx, item, index) {
  const locals = /* @__PURE__ */ new Map();
  locals.set("item", item);
  locals.set("index", index);
  return evaluate2(expr, createChildContext(ctx, locals));
}
function evalMap(args, evaluate2, ctx) {
  const collection = toArray(evaluate2(args[0], ctx));
  const mapExpr = args[1];
  return collection.map((item, i) => withItem(mapExpr, evaluate2, ctx, item, i));
}
function evalFilter(args, evaluate2, ctx) {
  const collection = toArray(evaluate2(args[0], ctx));
  const predExpr = args[1];
  return collection.filter((item, i) => Boolean(withItem(predExpr, evaluate2, ctx, item, i)));
}
function evalFind(args, evaluate2, ctx) {
  const collection = toArray(evaluate2(args[0], ctx));
  const predExpr = args[1];
  return collection.find((item, i) => Boolean(withItem(predExpr, evaluate2, ctx, item, i)));
}
function evalCount(args, evaluate2, ctx) {
  const collection = toArray(evaluate2(args[0], ctx));
  return collection.length;
}
function evalSum(args, evaluate2, ctx) {
  const collection = toArray(evaluate2(args[0], ctx));
  if (args.length === 1) {
    return collection.reduce((sum, item) => sum + toNumber2(item), 0);
  }
  const mapExpr = args[1];
  return collection.reduce((sum, item, i) => {
    const locals = /* @__PURE__ */ new Map();
    locals.set("item", item);
    locals.set("index", i);
    const childCtx = createChildContext(ctx, locals);
    return sum + toNumber2(evaluate2(mapExpr, childCtx));
  }, 0);
}
function evalFirst(args, evaluate2, ctx) {
  const collection = toArray(evaluate2(args[0], ctx));
  return collection.length > 0 ? collection[0] : void 0;
}
function evalLast(args, evaluate2, ctx) {
  const collection = toArray(evaluate2(args[0], ctx));
  return collection.length > 0 ? collection[collection.length - 1] : void 0;
}
function evalNth(args, evaluate2, ctx) {
  const collection = toArray(evaluate2(args[0], ctx));
  const index = toNumber2(evaluate2(args[1], ctx));
  return collection[index];
}
function evalConcat(args, evaluate2, ctx) {
  const result = [];
  for (const arg of args) {
    const collection = toArray(evaluate2(arg, ctx));
    result.push(...collection);
  }
  return result;
}
function evalIncludes(args, evaluate2, ctx) {
  const collection = toArray(evaluate2(args[0], ctx));
  const element = evaluate2(args[1], ctx);
  return collection.includes(element);
}
function evalEmpty(args, evaluate2, ctx) {
  const collection = toArray(evaluate2(args[0], ctx));
  return collection.length === 0;
}
function evalList(args, evaluate2, ctx) {
  return args.map((arg) => evaluate2(arg, ctx));
}
function toArray(value) {
  if (Array.isArray(value)) return value;
  if (value === null || value === void 0) return [];
  return [value];
}
function toNumber2(value) {
  if (typeof value === "number") return value;
  if (typeof value === "string") {
    const parsed = parseFloat(value);
    return isNaN(parsed) ? 0 : parsed;
  }
  return 0;
}

// operators/effects.ts
function evalSet(args, evaluate2, ctx) {
  const binding = args[0];
  const value = evaluate2(args[1], ctx);
  const operation = args[2];
  if (!ctx.mutateEntity) {
    console.warn("No mutateEntity handler in context for set effect");
    return;
  }
  if (!binding.startsWith("@entity.")) {
    console.warn(`set only supports @entity bindings, got: ${binding}`);
    return;
  }
  const fieldPath = binding.slice(8);
  let finalValue = value;
  if (operation) {
    const currentValue = resolveBinding(binding, ctx);
    switch (operation) {
      case "increment":
        finalValue = toNumber3(currentValue) + toNumber3(value);
        break;
      case "decrement":
        finalValue = toNumber3(currentValue) - toNumber3(value);
        break;
      case "multiply":
        finalValue = toNumber3(currentValue) * toNumber3(value);
        break;
      case "append":
        finalValue = Array.isArray(currentValue) ? [...currentValue, value] : [value];
        break;
      case "remove":
        finalValue = Array.isArray(currentValue) ? currentValue.filter((v) => v !== value) : [];
        break;
    }
  }
  ctx.mutateEntity({ [fieldPath]: finalValue });
}
function evalEmit(args, evaluate2, ctx) {
  const eventKey = args[0];
  const payload = args.length > 1 ? evaluate2(args[1], ctx) : void 0;
  if (!ctx.emit) {
    console.warn("No emit handler in context for emit effect");
    return;
  }
  ctx.emit(eventKey, payload);
}
function evalPersist(args, evaluate2, ctx) {
  const action = args[0];
  if (!ctx.persist) {
    console.warn("No persist handler in context for persist effect");
    return;
  }
  if (action === "batch") {
    const rawOps = args[1];
    if (!Array.isArray(rawOps)) {
      console.warn("persist batch requires an array of operations as second argument");
      return;
    }
    const operations = rawOps.map((op) => {
      if (!Array.isArray(op)) return op;
      return op.map((item) => evaluate2(item, ctx));
    });
    ctx.persist("batch", { operations }).catch((err) => {
      console.error("Persist batch failed:", err);
    });
    return;
  }
  const data = args.length > 1 ? evaluate2(args[1], ctx) : ctx.payload;
  ctx.persist(action, data).catch((err) => {
    console.error(`Persist ${action} failed:`, err);
  });
}
function evalNavigate(args, evaluate2, ctx) {
  const route = args[0];
  const params = args.length > 1 ? evaluate2(args[1], ctx) : void 0;
  if (!ctx.navigate) {
    console.warn("No navigate handler in context for navigate effect");
    return;
  }
  ctx.navigate(route, params);
}
function evalNotify(args, evaluate2, ctx) {
  const message = String(evaluate2(args[0], ctx));
  const type = args[1] || "info";
  if (!ctx.notify) {
    console.warn("No notify handler in context for notify effect");
    return;
  }
  ctx.notify(message, type);
}
function evalSpawn(args, evaluate2, ctx) {
  const entityType = args[0];
  const props = args.length > 1 ? evaluate2(args[1], ctx) : void 0;
  if (!ctx.spawn) {
    console.warn("No spawn handler in context for spawn effect");
    return;
  }
  ctx.spawn(entityType, props);
}
function evalDespawn(args, evaluate2, ctx) {
  const entityId = args.length > 0 ? String(evaluate2(args[0], ctx)) : void 0;
  if (!ctx.despawn) {
    console.warn("No despawn handler in context for despawn effect");
    return;
  }
  ctx.despawn(entityId);
}
function evalCallService(args, evaluate2, ctx) {
  const service = args[0];
  const method = args[1];
  const params = args.length > 2 ? evaluate2(args[2], ctx) : void 0;
  if (!ctx.callService) {
    console.warn("No callService handler in context for call-service effect");
    return;
  }
  ctx.callService(service, method, params).catch((err) => {
    console.error(`Service call ${service}.${method} failed:`, err);
  });
}
function evaluateNestedProps(obj, evaluate2, ctx) {
  const result = {};
  for (const [key2, value] of Object.entries(obj)) {
    if (Array.isArray(value) && value.length > 0 && typeof value[0] === "string") {
      result[key2] = evaluate2(value, ctx);
    } else if (typeof value === "object" && value !== null && !Array.isArray(value)) {
      result[key2] = evaluateNestedProps(value, evaluate2, ctx);
    } else {
      result[key2] = value;
    }
  }
  return result;
}
function evalRenderUI(args, evaluate2, ctx) {
  const slot = args[0];
  const pattern = evaluate2(args[1], ctx);
  const rawProps = args.length > 2 ? args[2] : void 0;
  const priority = args.length > 3 ? evaluate2(args[3], ctx) : void 0;
  if (!ctx.renderUI) {
    console.warn("No renderUI handler in context for render-ui effect");
    return;
  }
  if (pattern === null || pattern === void 0) {
    ctx.renderUI(slot, { type: "clear" }, void 0, priority);
    return;
  }
  const props = rawProps ? evaluateNestedProps(rawProps, evaluate2, ctx) : void 0;
  ctx.renderUI(slot, pattern, props, priority);
}
function evalSetDynamic(args, evaluate2, ctx) {
  const pathExpr = evaluate2(args[0], ctx);
  const value = evaluate2(args[1], ctx);
  if (!ctx.mutateEntity) {
    console.warn("No mutateEntity handler in context for set-dynamic effect");
    return;
  }
  if (!pathExpr || typeof pathExpr !== "string") {
    console.warn(`set-dynamic requires a valid path string, got: ${pathExpr}`);
    return;
  }
  ctx.mutateEntity({ [pathExpr]: value });
}
function evalIncrement(args, evaluate2, ctx) {
  const binding = args[0];
  const amount = args.length > 1 ? toNumber3(evaluate2(args[1], ctx)) : 1;
  if (!ctx.mutateEntity) {
    console.warn("No mutateEntity handler in context for increment effect");
    return;
  }
  if (!binding.startsWith("@entity.")) {
    console.warn(`increment only supports @entity bindings, got: ${binding}`);
    return;
  }
  const fieldPath = binding.slice(8);
  const currentValue = resolveBinding(binding, ctx);
  const newValue = toNumber3(currentValue) + amount;
  ctx.mutateEntity({ [fieldPath]: newValue });
}
function evalDecrement(args, evaluate2, ctx) {
  const binding = args[0];
  const amount = args.length > 1 ? toNumber3(evaluate2(args[1], ctx)) : 1;
  if (!ctx.mutateEntity) {
    console.warn("No mutateEntity handler in context for decrement effect");
    return;
  }
  if (!binding.startsWith("@entity.")) {
    console.warn(`decrement only supports @entity bindings, got: ${binding}`);
    return;
  }
  const fieldPath = binding.slice(8);
  const currentValue = resolveBinding(binding, ctx);
  const newValue = toNumber3(currentValue) - amount;
  ctx.mutateEntity({ [fieldPath]: newValue });
}
function evalRef(args, evaluate2, ctx) {
  const entityType = args[0];
  if (ctx.effectHandlers?.ref) {
    return ctx.effectHandlers.ref(entityType, args.length > 1 ? evaluate2(args[1], ctx) : void 0);
  }
  if (ctx.effectHandlers?.fetch) {
    return ctx.effectHandlers.fetch(entityType, args.length > 1 ? evaluate2(args[1], ctx) : void 0);
  }
  return void 0;
}
function evalDeref(args, evaluate2, ctx) {
  const entityType = args[0];
  const id = args.length > 1 ? evaluate2(args[1], ctx) : void 0;
  if (ctx.effectHandlers?.deref) {
    return ctx.effectHandlers.deref(entityType, id);
  }
  if (id && ctx.effectHandlers?.fetch) {
    return ctx.effectHandlers.fetch(entityType, id);
  }
  return ctx.entity[entityType] ?? [];
}
function evalSwap(args, evaluate2, ctx) {
  const entityType = args[0];
  const id = evaluate2(args[1], ctx);
  const transformExpr = args[2];
  if (ctx.effectHandlers?.swap) {
    return ctx.effectHandlers.swap(entityType, id, transformExpr, evaluate2, ctx);
  }
  console.warn("No swap handler in context");
  return void 0;
}
function evalWatch(args, evaluate2, ctx) {
  const entityType = args[0];
  const effects = args[1];
  if (ctx.effectHandlers?.watch) {
    ctx.effectHandlers.watch(entityType, effects, evaluate2, ctx);
  }
}
function evalAtomic(args, evaluate2, ctx) {
  const effects = args[0];
  if (ctx.effectHandlers?.atomic) {
    return ctx.effectHandlers.atomic(effects, evaluate2, ctx);
  }
  if (Array.isArray(effects)) {
    for (const eff of effects) {
      evaluate2(eff, ctx);
    }
  }
  return void 0;
}
function toNumber3(value) {
  if (typeof value === "number") return value;
  if (typeof value === "string") {
    const parsed = parseFloat(value);
    return isNaN(parsed) ? 0 : parsed;
  }
  return 0;
}

// std/math.ts
function evalMathAbs(args, evaluate2, ctx) {
  const n = evaluate2(args[0], ctx);
  return Math.abs(n);
}
function evalMathMin(args, evaluate2, ctx) {
  const values = args.map((a) => evaluate2(a, ctx));
  return Math.min(...values);
}
function evalMathMax(args, evaluate2, ctx) {
  const values = args.map((a) => evaluate2(a, ctx));
  return Math.max(...values);
}
function evalMathClamp(args, evaluate2, ctx) {
  const n = evaluate2(args[0], ctx);
  const min = evaluate2(args[1], ctx);
  const max = evaluate2(args[2], ctx);
  return Math.min(Math.max(n, min), max);
}
function evalMathFloor(args, evaluate2, ctx) {
  const n = evaluate2(args[0], ctx);
  return Math.floor(n);
}
function evalMathCeil(args, evaluate2, ctx) {
  const n = evaluate2(args[0], ctx);
  return Math.ceil(n);
}
function evalMathRound(args, evaluate2, ctx) {
  const n = evaluate2(args[0], ctx);
  const decimals = args.length > 1 ? evaluate2(args[1], ctx) : 0;
  if (decimals === 0) {
    return Math.round(n);
  }
  const factor = Math.pow(10, decimals);
  return Math.round(n * factor) / factor;
}
function evalMathPow(args, evaluate2, ctx) {
  const base = evaluate2(args[0], ctx);
  const exp = evaluate2(args[1], ctx);
  return Math.pow(base, exp);
}
function evalMathSqrt(args, evaluate2, ctx) {
  const n = evaluate2(args[0], ctx);
  return Math.sqrt(n);
}
function evalMathLog10(args, evaluate2, ctx) {
  const n = evaluate2(args[0], ctx);
  return Math.log10(n);
}
function evalMathLn(args, evaluate2, ctx) {
  const n = evaluate2(args[0], ctx);
  return Math.log(n);
}
function evalMathMod(args, evaluate2, ctx) {
  const a = evaluate2(args[0], ctx);
  const b = evaluate2(args[1], ctx);
  return a % b;
}
function evalMathSign(args, evaluate2, ctx) {
  const n = evaluate2(args[0], ctx);
  return Math.sign(n);
}
function evalMathLerp(args, evaluate2, ctx) {
  const a = evaluate2(args[0], ctx);
  const b = evaluate2(args[1], ctx);
  const t = evaluate2(args[2], ctx);
  return a + (b - a) * t;
}
function evalMathMap(args, evaluate2, ctx) {
  const n = evaluate2(args[0], ctx);
  const inMin = evaluate2(args[1], ctx);
  const inMax = evaluate2(args[2], ctx);
  const outMin = evaluate2(args[3], ctx);
  const outMax = evaluate2(args[4], ctx);
  return (n - inMin) / (inMax - inMin) * (outMax - outMin) + outMin;
}
function evalMathRandom() {
  return Math.random();
}
function evalMathRandomInt(args, evaluate2, ctx) {
  const min = evaluate2(args[0], ctx);
  const max = evaluate2(args[1], ctx);
  return Math.floor(Math.random() * (max - min + 1)) + min;
}
function evalMathDefault(args, evaluate2, ctx) {
  const n = evaluate2(args[0], ctx);
  const defaultValue = evaluate2(args[1], ctx);
  if (n === null || n === void 0 || typeof n === "number" && isNaN(n)) {
    return defaultValue;
  }
  return n;
}
function evalMathSin(args, evaluate2, ctx) {
  return Math.sin(evaluate2(args[0], ctx));
}
function evalMathCos(args, evaluate2, ctx) {
  return Math.cos(evaluate2(args[0], ctx));
}
function evalMathTan(args, evaluate2, ctx) {
  return Math.tan(evaluate2(args[0], ctx));
}
function evalMathAsin(args, evaluate2, ctx) {
  return Math.asin(evaluate2(args[0], ctx));
}
function evalMathAcos(args, evaluate2, ctx) {
  return Math.acos(evaluate2(args[0], ctx));
}
function evalMathAtan(args, evaluate2, ctx) {
  return Math.atan(evaluate2(args[0], ctx));
}
function evalMathAtan2(args, evaluate2, ctx) {
  const y = evaluate2(args[0], ctx);
  const x = evaluate2(args[1], ctx);
  return Math.atan2(y, x);
}
function evalMathHypot(args, evaluate2, ctx) {
  const a = evaluate2(args[0], ctx);
  const b = evaluate2(args[1], ctx);
  return Math.hypot(a, b);
}
function evalMathDegRad(args, evaluate2, ctx) {
  return evaluate2(args[0], ctx) * (Math.PI / 180);
}
function evalMathRadDeg(args, evaluate2, ctx) {
  return evaluate2(args[0], ctx) * (180 / Math.PI);
}
function evalMathWrap(args, evaluate2, ctx) {
  const v = evaluate2(args[0], ctx);
  const min = evaluate2(args[1], ctx);
  const max = evaluate2(args[2], ctx);
  const r = max - min;
  if (r <= 0) {
    return min;
  }
  return min + ((v - min) % r + r) % r;
}
function evalMathApproach(args, evaluate2, ctx) {
  const cur = evaluate2(args[0], ctx);
  const target = evaluate2(args[1], ctx);
  const maxD = evaluate2(args[2], ctx);
  if (Math.abs(target - cur) <= maxD) {
    return target;
  }
  return cur + Math.sign(target - cur) * maxD;
}

// std/str.ts
function evalStrLen(args, evaluate2, ctx) {
  const s = evaluate2(args[0], ctx);
  return s?.length ?? 0;
}
function evalStrConcat(args, evaluate2, ctx) {
  return args.map((arg) => {
    const val = evaluate2(arg, ctx);
    return val !== null && val !== void 0 ? String(val) : "";
  }).join("");
}
function evalStrUpper(args, evaluate2, ctx) {
  const s = evaluate2(args[0], ctx);
  return s?.toUpperCase() ?? "";
}
function evalStrLower(args, evaluate2, ctx) {
  const s = evaluate2(args[0], ctx);
  return s?.toLowerCase() ?? "";
}
function evalStrTrim(args, evaluate2, ctx) {
  const s = evaluate2(args[0], ctx);
  return s?.trim() ?? "";
}
function evalStrTrimStart(args, evaluate2, ctx) {
  const s = evaluate2(args[0], ctx);
  return s?.trimStart() ?? "";
}
function evalStrTrimEnd(args, evaluate2, ctx) {
  const s = evaluate2(args[0], ctx);
  return s?.trimEnd() ?? "";
}
function evalStrSplit(args, evaluate2, ctx) {
  const s = evaluate2(args[0], ctx);
  const delim = evaluate2(args[1], ctx);
  return s?.split(delim) ?? [];
}
function evalStrJoin(args, evaluate2, ctx) {
  const arr = evaluate2(args[0], ctx);
  const delim = evaluate2(args[1], ctx);
  return arr?.join(delim) ?? "";
}
function evalStrSlice(args, evaluate2, ctx) {
  const s = evaluate2(args[0], ctx);
  const start = evaluate2(args[1], ctx);
  const end = args.length > 2 ? evaluate2(args[2], ctx) : void 0;
  return s?.slice(start, end) ?? "";
}
function evalStrReplace(args, evaluate2, ctx) {
  const s = evaluate2(args[0], ctx);
  const find = evaluate2(args[1], ctx);
  const replacement = evaluate2(args[2], ctx);
  return s?.replace(find, replacement) ?? "";
}
function evalStrReplaceAll(args, evaluate2, ctx) {
  const s = evaluate2(args[0], ctx);
  const find = evaluate2(args[1], ctx);
  const replacement = evaluate2(args[2], ctx);
  if (!s || !find) return s ?? "";
  const escaped = find.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  return s.replace(new RegExp(escaped, "g"), replacement);
}
function evalStrIncludes(args, evaluate2, ctx) {
  const s = evaluate2(args[0], ctx);
  const search = evaluate2(args[1], ctx);
  return s?.includes(search) ?? false;
}
function evalStrStartsWith(args, evaluate2, ctx) {
  const s = evaluate2(args[0], ctx);
  const prefix = evaluate2(args[1], ctx);
  return s?.startsWith(prefix) ?? false;
}
function evalStrEndsWith(args, evaluate2, ctx) {
  const s = evaluate2(args[0], ctx);
  const suffix = evaluate2(args[1], ctx);
  return s?.endsWith(suffix) ?? false;
}
function evalStrPadStart(args, evaluate2, ctx) {
  const s = evaluate2(args[0], ctx);
  const len = evaluate2(args[1], ctx);
  const char = args.length > 2 ? evaluate2(args[2], ctx) : " ";
  return s?.padStart(len, char) ?? "";
}
function evalStrPadEnd(args, evaluate2, ctx) {
  const s = evaluate2(args[0], ctx);
  const len = evaluate2(args[1], ctx);
  const char = args.length > 2 ? evaluate2(args[2], ctx) : " ";
  return s?.padEnd(len, char) ?? "";
}
function evalStrRepeat(args, evaluate2, ctx) {
  const s = evaluate2(args[0], ctx);
  const count = evaluate2(args[1], ctx);
  return s?.repeat(count) ?? "";
}
function evalStrReverse(args, evaluate2, ctx) {
  const s = evaluate2(args[0], ctx);
  return s?.split("").reverse().join("") ?? "";
}
function evalStrCapitalize(args, evaluate2, ctx) {
  const s = evaluate2(args[0], ctx);
  if (!s || s.length === 0) return "";
  return s.charAt(0).toUpperCase() + s.slice(1);
}
function evalStrTitleCase(args, evaluate2, ctx) {
  const s = evaluate2(args[0], ctx);
  if (!s) return "";
  return s.replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substring(1).toLowerCase());
}
function evalStrCamelCase(args, evaluate2, ctx) {
  const s = evaluate2(args[0], ctx);
  if (!s) return "";
  return s.replace(
    /(?:^\w|[A-Z]|\b\w)/g,
    (word, index) => index === 0 ? word.toLowerCase() : word.toUpperCase()
  ).replace(/\s+/g, "").replace(/[-_]/g, "");
}
function evalStrKebabCase(args, evaluate2, ctx) {
  const s = evaluate2(args[0], ctx);
  if (!s) return "";
  return s.replace(/([a-z])([A-Z])/g, "$1-$2").replace(/[\s_]+/g, "-").toLowerCase();
}
function evalStrSnakeCase(args, evaluate2, ctx) {
  const s = evaluate2(args[0], ctx);
  if (!s) return "";
  return s.replace(/([a-z])([A-Z])/g, "$1_$2").replace(/[\s-]+/g, "_").toLowerCase();
}
function evalStrDefault(args, evaluate2, ctx) {
  const s = evaluate2(args[0], ctx);
  const defaultValue = evaluate2(args[1], ctx);
  if (s === null || s === void 0 || s === "") {
    return defaultValue;
  }
  return s;
}
function evalStrTemplate(args, evaluate2, ctx) {
  const template = evaluate2(args[0], ctx);
  const vars = evaluate2(args[1], ctx);
  if (!template) return "";
  return template.replace(/\{(\w+)\}/g, (_, key2) => {
    const value = vars?.[key2];
    return value !== void 0 ? String(value) : "";
  });
}
function evalStrTruncate(args, evaluate2, ctx) {
  const s = evaluate2(args[0], ctx);
  const len = evaluate2(args[1], ctx);
  const suffix = args.length > 2 ? evaluate2(args[2], ctx) : "...";
  if (!s || s.length <= len) return s ?? "";
  return s.slice(0, len - suffix.length) + suffix;
}

// errors.ts
function runtimeTypeName(value) {
  if (value === null || value === void 0) return "null";
  if (Array.isArray(value)) return "array";
  if (typeof value === "function") return "function";
  if (typeof value === "object") return "object";
  return typeof value;
}
var EvalTypeMismatchError = class extends Error {
  constructor(expected, actual) {
    super(`Type mismatch: expected ${expected}, got ${actual}`);
    this.name = "EvalTypeMismatchError";
    this.expected = expected;
    this.actual = actual;
  }
};

// std/array.ts
function toNumber4(value) {
  if (typeof value === "number") return value;
  if (typeof value === "boolean") return value ? 1 : 0;
  if (value === null) return 0;
  if (typeof value === "string" && value !== "" && !Number.isNaN(Number(value))) return Number(value);
  return void 0;
}
function numbersOf(args, evaluate2, ctx) {
  const arr = requireArray(evaluate2(args[0], ctx));
  const key2 = args.length > 1 ? evaluate2(args[1], ctx) : void 0;
  if (key2 !== void 0 && typeof key2 !== "string") throw new EvalTypeMismatchError("string", runtimeTypeName(key2));
  const out = [];
  for (const item of arr) {
    const value = key2 === void 0 ? item : fieldOf(item, key2);
    const n = value === void 0 ? void 0 : toNumber4(value);
    if (n !== void 0) out.push(n);
  }
  return out;
}
function fieldOf(item, key2) {
  if (item === null || typeof item !== "object" || Array.isArray(item) || !(key2 in item)) return void 0;
  return Reflect.get(item, key2);
}
function requireArray(value) {
  if (Array.isArray(value)) return value;
  throw new EvalTypeMismatchError("array", runtimeTypeName(value));
}
function evalWithItem(expr, evaluate2, ctx, item, index) {
  const locals = /* @__PURE__ */ new Map();
  locals.set("item", item);
  locals.set("index", index);
  if (isSExpr(expr)) {
    const op = getOperator(expr);
    if (op === "fn" || op === "lambda") {
      const fnArgs = getArgs(expr);
      const params = fnArgs[0];
      const body = fnArgs[1];
      if (typeof params === "string") {
        const key2 = params.startsWith("@") ? params.slice(1) : params;
        locals.set(key2, item);
      } else if (Array.isArray(params)) {
        const paramNames = params;
        const values = [item, index];
        for (let i = 0; i < paramNames.length; i++) {
          const p2 = paramNames[i];
          const key2 = p2.startsWith("@") ? p2.slice(1) : p2;
          locals.set(key2, values[i]);
        }
      }
      const childCtx2 = createChildContext(ctx, locals);
      return evaluate2(body, childCtx2);
    }
  }
  const childCtx = createChildContext(ctx, locals);
  let result = evaluate2(expr, childCtx);
  if (isSExpr(result)) {
    result = evalWithItem(result, evaluate2, childCtx, item, index);
  } else if (typeof result === "function") {
    result = result(item, evaluate2, childCtx);
  }
  return result;
}
function evalReduceLambda(lambdaExpr, evaluate2, ctx, acc, item) {
  const locals = /* @__PURE__ */ new Map();
  if (isSExpr(lambdaExpr) && getOperator(lambdaExpr) === "fn") {
    const fnArgs = getArgs(lambdaExpr);
    const params = fnArgs[0];
    const body = fnArgs[1];
    if (Array.isArray(params)) {
      const paramNames = params;
      const values = [acc, item];
      paramNames.forEach((p2, i) => {
        const key2 = p2.startsWith("@") ? p2.slice(1) : p2;
        locals.set(key2, values[i]);
      });
    } else if (typeof params === "string") {
      const key2 = params.startsWith("@") ? params.slice(1) : params;
      locals.set(key2, acc);
    }
    const childCtx2 = createChildContext(ctx, locals);
    return evaluate2(body, childCtx2);
  }
  locals.set("acc", acc);
  locals.set("item", item);
  const childCtx = createChildContext(ctx, locals);
  return evaluate2(lambdaExpr, childCtx);
}
function evalArrayLen(args, evaluate2, ctx) {
  const arr = evaluate2(args[0], ctx);
  return arr?.length ?? 0;
}
function evalArrayRange(args, evaluate2, ctx) {
  const first = Number(evaluate2(args[0], ctx));
  const start = args.length === 1 ? 0 : first;
  const end = args.length === 1 ? first : Number(evaluate2(args[1], ctx));
  const step = args.length > 2 ? Number(evaluate2(args[2], ctx)) : 1;
  const out = [];
  if (!Number.isFinite(start) || !Number.isFinite(end) || !Number.isFinite(step) || step === 0) {
    return out;
  }
  if (step > 0) {
    for (let current = start; current < end; current += step) out.push(current);
  } else {
    for (let current = start; current > end; current += step) out.push(current);
  }
  return out;
}
function evalArrayFirst(args, evaluate2, ctx) {
  const arr = evaluate2(args[0], ctx);
  return arr?.[0] ?? null;
}
function evalArrayLast(args, evaluate2, ctx) {
  const arr = evaluate2(args[0], ctx);
  if (!Array.isArray(arr) || arr.length === 0) return null;
  return arr[arr.length - 1];
}
function evalArrayNth(args, evaluate2, ctx) {
  const arr = evaluate2(args[0], ctx);
  const index = evaluate2(args[1], ctx);
  return arr?.[index] ?? null;
}
function evalArraySlice(args, evaluate2, ctx) {
  const arr = requireArray(evaluate2(args[0], ctx));
  const start = evaluate2(args[1], ctx);
  const end = args.length > 2 ? evaluate2(args[2], ctx) : void 0;
  return arr.slice(start, end);
}
function evalArrayConcat(args, evaluate2, ctx) {
  const arrays = args.map((a) => evaluate2(a, ctx));
  return arrays.reduce((acc, arr) => acc.concat(arr ?? []), []);
}
function evalArrayAppend(args, evaluate2, ctx) {
  const arr = requireArray(evaluate2(args[0], ctx));
  const item = evaluate2(args[1], ctx);
  return [...arr, item];
}
function evalArrayPrepend(args, evaluate2, ctx) {
  const arr = requireArray(evaluate2(args[0], ctx));
  const item = evaluate2(args[1], ctx);
  return [item, ...arr];
}
function evalArrayInsert(args, evaluate2, ctx) {
  const arr = requireArray(evaluate2(args[0], ctx));
  const index = evaluate2(args[1], ctx);
  const item = evaluate2(args[2], ctx);
  const result = [...arr];
  result.splice(index, 0, item);
  return result;
}
function evalArrayRemove(args, evaluate2, ctx) {
  const arr = requireArray(evaluate2(args[0], ctx));
  const index = evaluate2(args[1], ctx);
  const result = [...arr];
  result.splice(index, 1);
  return result;
}
function evalArrayRemoveItem(args, evaluate2, ctx) {
  const arr = evaluate2(args[0], ctx);
  const item = evaluate2(args[1], ctx);
  const index = arr?.indexOf(item) ?? -1;
  if (index === -1) return arr ?? [];
  const result = [...arr];
  result.splice(index, 1);
  return result;
}
function evalArrayReverse(args, evaluate2, ctx) {
  const arr = requireArray(evaluate2(args[0], ctx));
  return [...arr].reverse();
}
function evalArraySort(args, evaluate2, ctx) {
  const arr = requireArray(evaluate2(args[0], ctx));
  const key2 = args.length > 1 ? evaluate2(args[1], ctx) : void 0;
  const dir = args.length > 2 ? evaluate2(args[2], ctx) : "asc";
  const result = [...arr];
  if (key2) {
    result.sort((a, b) => {
      const aVal = a[key2];
      const bVal = b[key2];
      if (aVal < bVal) return dir === "asc" ? -1 : 1;
      if (aVal > bVal) return dir === "asc" ? 1 : -1;
      return 0;
    });
  } else {
    result.sort((a, b) => {
      const aVal = a;
      const bVal = b;
      if (aVal < bVal) return dir === "asc" ? -1 : 1;
      if (aVal > bVal) return dir === "asc" ? 1 : -1;
      return 0;
    });
  }
  return result;
}
function evalArrayShuffle(args, evaluate2, ctx) {
  const arr = requireArray(evaluate2(args[0], ctx));
  const result = [...arr];
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}
function evalArrayUnique(args, evaluate2, ctx) {
  const arr = requireArray(evaluate2(args[0], ctx));
  return [...new Set(arr)];
}
function evalArrayFlatten(args, evaluate2, ctx) {
  const arr = requireArray(evaluate2(args[0], ctx));
  return arr.flat();
}
function evalArrayZip(args, evaluate2, ctx) {
  const arr1 = evaluate2(args[0], ctx);
  const arr2 = evaluate2(args[1], ctx);
  const len = Math.min(arr1?.length ?? 0, arr2?.length ?? 0);
  const result = [];
  for (let i = 0; i < len; i++) {
    result.push([arr1[i], arr2[i]]);
  }
  return result;
}
function evalArrayIncludes(args, evaluate2, ctx) {
  const arr = evaluate2(args[0], ctx);
  const item = evaluate2(args[1], ctx);
  return arr?.includes(item) ?? false;
}
function evalArrayIndexOf(args, evaluate2, ctx) {
  const arr = requireArray(evaluate2(args[0], ctx));
  const item = evaluate2(args[1], ctx);
  return arr.indexOf(item);
}
function evalArrayFind(args, evaluate2, ctx) {
  const arr = requireArray(evaluate2(args[0], ctx));
  const predExpr = args[1];
  return arr.find((item, i) => evalWithItem(predExpr, evaluate2, ctx, item, i)) ?? null;
}
function evalArrayFindIndex(args, evaluate2, ctx) {
  const arr = requireArray(evaluate2(args[0], ctx));
  const predExpr = args[1];
  return arr.findIndex((item, i) => evalWithItem(predExpr, evaluate2, ctx, item, i));
}
function evalArrayFilter(args, evaluate2, ctx) {
  const arr = requireArray(evaluate2(args[0], ctx));
  const predExpr = args[1];
  return arr.filter((item, i) => evalWithItem(predExpr, evaluate2, ctx, item, i));
}
function evalArrayReject(args, evaluate2, ctx) {
  const arr = requireArray(evaluate2(args[0], ctx));
  const predExpr = args[1];
  return arr.filter((item, i) => !evalWithItem(predExpr, evaluate2, ctx, item, i));
}
function evalArrayMap(args, evaluate2, ctx) {
  const arr = requireArray(evaluate2(args[0], ctx));
  const mapExpr = args[1];
  return arr.map((item, i) => evalWithItem(mapExpr, evaluate2, ctx, item, i));
}
function evalArrayReduce(args, evaluate2, ctx) {
  if (isSExpr(args[1]) && getOperator(args[1]) === "fn" && !(isSExpr(args[2]) && getOperator(args[2]) === "fn")) {
    throw new Error(
      "(array/reduce \u2026): the reducer lambda goes at argument 3 \u2014 (array/reduce arr init (fn (acc x) \u2026)) \u2014 same order the compiled path enforces (SEXPR_LAMBDA_ARG_POSITION)"
    );
  }
  const arr = requireArray(evaluate2(args[0], ctx));
  const init = evaluate2(args[1], ctx);
  const reducerExpr = args[2];
  return arr.reduce(
    (acc, item) => evalReduceLambda(reducerExpr, evaluate2, ctx, acc, item),
    init
  );
}
function evalArrayEvery(args, evaluate2, ctx) {
  const arr = requireArray(evaluate2(args[0], ctx));
  const predExpr = args[1];
  return arr.every((item, i) => Boolean(evalWithItem(predExpr, evaluate2, ctx, item, i)));
}
function evalArraySome(args, evaluate2, ctx) {
  const arr = requireArray(evaluate2(args[0], ctx));
  const predExpr = args[1];
  return arr.some((item, i) => Boolean(evalWithItem(predExpr, evaluate2, ctx, item, i)));
}
function evalArrayCount(args, evaluate2, ctx) {
  const arr = evaluate2(args[0], ctx);
  if (args.length > 1) {
    const predExpr = args[1];
    return (arr ?? []).filter((item, i) => evalWithItem(predExpr, evaluate2, ctx, item, i)).length;
  }
  return arr?.length ?? 0;
}
function evalArraySum(args, evaluate2, ctx) {
  const arr = requireArray(evaluate2(args[0], ctx));
  const key2 = args.length > 1 ? evaluate2(args[1], ctx) : void 0;
  return arr.reduce((sum, item) => {
    const value = key2 ? item[key2] : item;
    return sum + (typeof value === "number" ? value : 0);
  }, 0);
}
function evalArrayAvg(args, evaluate2, ctx) {
  const arr = requireArray(evaluate2(args[0], ctx));
  if (arr.length === 0) return 0;
  const key2 = args.length > 1 ? evaluate2(args[1], ctx) : void 0;
  const sum = arr.reduce((s, item) => {
    const value = key2 ? item[key2] : item;
    return s + (typeof value === "number" ? value : 0);
  }, 0);
  return sum / arr.length;
}
function evalArrayMin(args, evaluate2, ctx) {
  const numbers = numbersOf(args, evaluate2, ctx);
  return numbers.length > 0 ? Math.min(...numbers) : null;
}
function evalArrayMax(args, evaluate2, ctx) {
  const numbers = numbersOf(args, evaluate2, ctx);
  return numbers.length > 0 ? Math.max(...numbers) : null;
}
function evalArrayGroupBy(args, evaluate2, ctx) {
  const arr = requireArray(evaluate2(args[0], ctx));
  const key2 = evaluate2(args[1], ctx);
  const result = {};
  for (const item of arr) {
    const groupKey = String(item[key2] ?? "undefined");
    if (!result[groupKey]) {
      result[groupKey] = [];
    }
    result[groupKey].push(item);
  }
  return result;
}
function evalArrayPartition(args, evaluate2, ctx) {
  const arr = requireArray(evaluate2(args[0], ctx));
  const predExpr = args[1];
  const matches = [];
  const nonMatches = [];
  arr.forEach((item, i) => {
    if (evalWithItem(predExpr, evaluate2, ctx, item, i)) {
      matches.push(item);
    } else {
      nonMatches.push(item);
    }
  });
  return [matches, nonMatches];
}
function evalArrayTake(args, evaluate2, ctx) {
  const arr = requireArray(evaluate2(args[0], ctx));
  const n = evaluate2(args[1], ctx);
  return arr.slice(0, n);
}
function evalArrayDrop(args, evaluate2, ctx) {
  const arr = requireArray(evaluate2(args[0], ctx));
  const n = evaluate2(args[1], ctx);
  return arr.slice(n);
}
function evalArrayTakeLast(args, evaluate2, ctx) {
  const arr = requireArray(evaluate2(args[0], ctx));
  const n = evaluate2(args[1], ctx);
  return arr.slice(-n);
}
function evalArrayDropLast(args, evaluate2, ctx) {
  const arr = requireArray(evaluate2(args[0], ctx));
  const n = evaluate2(args[1], ctx);
  return arr.slice(0, -n);
}
function cosineSimilarity(a, b) {
  if (a.length !== b.length) {
    throw new Error(`array/cosine: vector length mismatch (${a.length} vs ${b.length})`);
  }
  if (a.length === 0) {
    throw new Error("array/cosine: empty vector has no defined similarity");
  }
  let dot = 0;
  let normA = 0;
  let normB = 0;
  for (let i = 0; i < a.length; i++) {
    const av = a[i];
    const bv = b[i];
    dot += av * bv;
    normA += av * av;
    normB += bv * bv;
  }
  if (normA === 0 || normB === 0) {
    throw new Error("array/cosine: zero-magnitude vector has no defined direction to compare");
  }
  return dot / (Math.sqrt(normA) * Math.sqrt(normB));
}
function evalArrayCosine(args, evaluate2, ctx) {
  const a = evaluate2(args[0], ctx);
  const b = evaluate2(args[1], ctx);
  return cosineSimilarity(a, b);
}
function evalArrayNearest(args, evaluate2, ctx) {
  const query = evaluate2(args[0], ctx);
  const candidates = evaluate2(args[1], ctx);
  if (candidates.length === 0) {
    throw new Error("array/nearest: candidates array is empty, no nearest match exists");
  }
  const scores = candidates.map((c) => cosineSimilarity(query, c));
  let bestIdx = 0;
  let bestScore = scores[0];
  for (let i = 1; i < scores.length; i++) {
    if (scores[i] > bestScore) {
      bestScore = scores[i];
      bestIdx = i;
    }
  }
  let secondBest = -Infinity;
  for (let i = 0; i < scores.length; i++) {
    if (i === bestIdx) continue;
    if (scores[i] > secondBest) secondBest = scores[i];
  }
  const margin = scores.length === 1 ? 0 : bestScore - secondBest;
  return { index: bestIdx, score: bestScore, margin };
}

// std/object.ts
var FORBIDDEN_KEYS = /* @__PURE__ */ new Set(["__proto__", "constructor", "prototype"]);
function evalLambda(lambdaExpr, evaluate2, ctx, ...values) {
  if (!isSExpr(lambdaExpr) || getOperator(lambdaExpr) !== "fn") {
    return evaluate2(lambdaExpr, ctx);
  }
  const args = getArgs(lambdaExpr);
  const params = args[0];
  const body = args[1];
  const newLocals = /* @__PURE__ */ new Map();
  if (Array.isArray(params)) {
    const paramNames = params;
    values.forEach((v, i) => {
      if (paramNames[i]) {
        const paramName = paramNames[i];
        const key2 = paramName.startsWith("@") ? paramName.slice(1) : paramName;
        newLocals.set(key2, v);
      }
    });
  } else if (typeof params === "string") {
    const paramName = params.startsWith("@") ? params.slice(1) : params;
    newLocals.set(paramName, values[0]);
  }
  const childCtx = createChildContext(ctx, newLocals);
  return evaluate2(body, childCtx);
}
function evalObjectKeys(args, evaluate2, ctx) {
  const obj = evaluate2(args[0], ctx);
  return Object.keys(obj ?? {});
}
function evalObjectValues(args, evaluate2, ctx) {
  const obj = evaluate2(args[0], ctx);
  return Object.values(obj ?? {});
}
function evalObjectEntries(args, evaluate2, ctx) {
  const obj = evaluate2(args[0], ctx);
  return Object.entries(obj ?? {});
}
function evalObjectFromEntries(args, evaluate2, ctx) {
  const entries = evaluate2(args[0], ctx);
  return Object.fromEntries(entries ?? []);
}
function evalObjectGet(args, evaluate2, ctx) {
  const obj = evaluate2(args[0], ctx);
  const path = evaluate2(args[1], ctx);
  const defaultValue = args.length > 2 ? evaluate2(args[2], ctx) : void 0;
  if (!obj || !path) return defaultValue;
  const parts = path.split(".");
  let current = obj;
  for (const part of parts) {
    if (current === null || current === void 0) {
      return defaultValue;
    }
    current = current[part];
  }
  return current !== void 0 ? current : defaultValue;
}
function evalObjectSet(args, evaluate2, ctx) {
  const obj = evaluate2(args[0], ctx);
  const path = evaluate2(args[1], ctx);
  const value = evaluate2(args[2], ctx);
  if (!path) return obj ?? {};
  const result = structuredClone(obj ?? {});
  const parts = path.split(".");
  if (parts.some((p2) => FORBIDDEN_KEYS.has(p2))) {
    return result;
  }
  let current = result;
  for (let i = 0; i < parts.length - 1; i++) {
    const part = parts[i];
    if (!current[part] || typeof current[part] !== "object") {
      current[part] = {};
    }
    current = current[part];
  }
  current[parts[parts.length - 1]] = value;
  return result;
}
function evalObjectHas(args, evaluate2, ctx) {
  const obj = evaluate2(args[0], ctx);
  const path = evaluate2(args[1], ctx);
  if (!obj || !path) return false;
  const parts = path.split(".");
  let current = obj;
  for (const part of parts) {
    if (current === null || current === void 0) {
      return false;
    }
    if (!Object.prototype.hasOwnProperty.call(current, part)) {
      return false;
    }
    current = current[part];
  }
  return true;
}
function evalObjectMerge(args, evaluate2, ctx) {
  const objects = args.map((a) => evaluate2(a, ctx));
  return Object.assign({}, ...objects.map((o) => o ?? {}));
}
function evalObjectDeepMerge(args, evaluate2, ctx) {
  const objects = args.map((a) => evaluate2(a, ctx));
  function deepMerge(target, source) {
    const result = { ...target };
    for (const key2 of Object.keys(source)) {
      if (FORBIDDEN_KEYS.has(key2)) continue;
      if (source[key2] !== null && typeof source[key2] === "object" && !Array.isArray(source[key2]) && target[key2] !== null && typeof target[key2] === "object" && !Array.isArray(target[key2])) {
        result[key2] = deepMerge(
          target[key2],
          source[key2]
        );
      } else {
        result[key2] = source[key2];
      }
    }
    return result;
  }
  return objects.reduce((acc, obj) => deepMerge(acc, obj ?? {}), {});
}
function evalObjectPick(args, evaluate2, ctx) {
  const obj = evaluate2(args[0], ctx);
  const keys = evaluate2(args[1], ctx);
  const result = {};
  for (const key2 of keys ?? []) {
    if (obj && Object.prototype.hasOwnProperty.call(obj, key2)) {
      result[key2] = obj[key2];
    }
  }
  return result;
}
function evalObjectOmit(args, evaluate2, ctx) {
  const obj = evaluate2(args[0], ctx);
  const keys = evaluate2(args[1], ctx);
  const keysSet = new Set(keys ?? []);
  const result = {};
  for (const key2 of Object.keys(obj ?? {})) {
    if (!keysSet.has(key2)) {
      result[key2] = obj[key2];
    }
  }
  return result;
}
function evalObjectMapValues(args, evaluate2, ctx) {
  const obj = evaluate2(args[0], ctx);
  const lambda = args[1];
  const result = {};
  for (const [key2, value] of Object.entries(obj ?? {})) {
    result[key2] = evalLambda(lambda, evaluate2, ctx, value);
  }
  return result;
}
function evalObjectMapKeys(args, evaluate2, ctx) {
  const obj = evaluate2(args[0], ctx);
  const lambda = args[1];
  const result = {};
  for (const [key2, value] of Object.entries(obj ?? {})) {
    const newKey = String(evalLambda(lambda, evaluate2, ctx, key2));
    result[newKey] = value;
  }
  return result;
}
function evalObjectFilter(args, evaluate2, ctx) {
  const obj = evaluate2(args[0], ctx);
  const lambda = args[1];
  const result = {};
  for (const [key2, value] of Object.entries(obj ?? {})) {
    if (evalLambda(lambda, evaluate2, ctx, key2, value)) {
      result[key2] = value;
    }
  }
  return result;
}
function evalObjectEquals(args, evaluate2, ctx) {
  const a = evaluate2(args[0], ctx);
  const b = evaluate2(args[1], ctx);
  function deepEqual2(x, y) {
    if (x === y) return true;
    if (typeof x !== typeof y) return false;
    if (typeof x !== "object" || x === null || y === null) return false;
    if (Array.isArray(x) !== Array.isArray(y)) return false;
    const xKeys = Object.keys(x);
    const yKeys = Object.keys(y);
    if (xKeys.length !== yKeys.length) return false;
    for (const key2 of xKeys) {
      if (!deepEqual2(x[key2], y[key2])) {
        return false;
      }
    }
    return true;
  }
  return deepEqual2(a, b);
}
function evalObjectClone(args, evaluate2, ctx) {
  const obj = evaluate2(args[0], ctx);
  return { ...obj ?? {} };
}
function evalObjectDeepClone(args, evaluate2, ctx) {
  const obj = evaluate2(args[0], ctx);
  return structuredClone(obj ?? {});
}
function evalPath(args, evaluate2, ctx) {
  if (args.length === 0) {
    throw new Error("path operator requires at least 1 argument");
  }
  const segments = args.map((arg) => {
    const evaluated = evaluate2(arg, ctx);
    return String(evaluated ?? "");
  });
  return segments.join(".");
}

// std/json.ts
function evalJsonParse(args, evaluate2, ctx) {
  const val = evaluate2(args[0], ctx);
  if (typeof val !== "string") return null;
  try {
    return JSON.parse(val);
  } catch {
    return null;
  }
}
function evalJsonStringify(args, evaluate2, ctx) {
  const val = evaluate2(args[0], ctx);
  try {
    const out = JSON.stringify(val);
    return out === void 0 ? null : out;
  } catch {
    return null;
  }
}

// std/validate.ts
function evalValidateRequired(args, evaluate2, ctx) {
  const value = evaluate2(args[0], ctx);
  return value !== null && value !== void 0 && value !== "";
}
function evalValidateString(args, evaluate2, ctx) {
  const value = evaluate2(args[0], ctx);
  return typeof value === "string";
}
function evalValidateNumber(args, evaluate2, ctx) {
  const value = evaluate2(args[0], ctx);
  return typeof value === "number" && !isNaN(value);
}
function evalValidateBoolean(args, evaluate2, ctx) {
  const value = evaluate2(args[0], ctx);
  return typeof value === "boolean";
}
function evalValidateArray(args, evaluate2, ctx) {
  const value = evaluate2(args[0], ctx);
  return Array.isArray(value);
}
function evalValidateObject(args, evaluate2, ctx) {
  const value = evaluate2(args[0], ctx);
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
function evalValidateEmail(args, evaluate2, ctx) {
  const value = evaluate2(args[0], ctx);
  if (typeof value !== "string") return false;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(value);
}
function evalValidateUrl(args, evaluate2, ctx) {
  const value = evaluate2(args[0], ctx);
  if (typeof value !== "string") return false;
  try {
    new URL(value);
    return true;
  } catch {
    return false;
  }
}
function evalValidateUuid(args, evaluate2, ctx) {
  const value = evaluate2(args[0], ctx);
  if (typeof value !== "string") return false;
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuidRegex.test(value);
}
function evalValidatePhone(args, evaluate2, ctx) {
  const value = evaluate2(args[0], ctx);
  if (typeof value !== "string") return false;
  const phoneRegex = /^\+?[\d\s\-().]{10,}$/;
  const digits = value.replace(/\D/g, "");
  return phoneRegex.test(value) && digits.length >= 10;
}
function evalValidateCreditCard(args, evaluate2, ctx) {
  const value = evaluate2(args[0], ctx);
  if (typeof value !== "string") return false;
  const digits = value.replace(/\D/g, "");
  if (digits.length < 13 || digits.length > 19) return false;
  let sum = 0;
  let isEven = false;
  for (let i = digits.length - 1; i >= 0; i--) {
    let digit = parseInt(digits[i], 10);
    if (isEven) {
      digit *= 2;
      if (digit > 9) digit -= 9;
    }
    sum += digit;
    isEven = !isEven;
  }
  return sum % 10 === 0;
}
function evalValidateDate(args, evaluate2, ctx) {
  const value = evaluate2(args[0], ctx);
  if (value instanceof Date) return !isNaN(value.getTime());
  if (typeof value === "number") return !isNaN(new Date(value).getTime());
  if (typeof value === "string") return !isNaN(new Date(value).getTime());
  return false;
}
function evalValidateMinLength(args, evaluate2, ctx) {
  const value = evaluate2(args[0], ctx);
  const min = evaluate2(args[1], ctx);
  if (typeof value === "string" || Array.isArray(value)) {
    return value.length >= min;
  }
  return false;
}
function evalValidateMaxLength(args, evaluate2, ctx) {
  const value = evaluate2(args[0], ctx);
  const max = evaluate2(args[1], ctx);
  if (typeof value === "string" || Array.isArray(value)) {
    return value.length <= max;
  }
  return false;
}
function evalValidateLength(args, evaluate2, ctx) {
  const value = evaluate2(args[0], ctx);
  const exact = evaluate2(args[1], ctx);
  if (typeof value === "string" || Array.isArray(value)) {
    return value.length === exact;
  }
  return false;
}
function evalValidateMin(args, evaluate2, ctx) {
  const value = evaluate2(args[0], ctx);
  const min = evaluate2(args[1], ctx);
  return typeof value === "number" && value >= min;
}
function evalValidateMax(args, evaluate2, ctx) {
  const value = evaluate2(args[0], ctx);
  const max = evaluate2(args[1], ctx);
  return typeof value === "number" && value <= max;
}
function evalValidateRange(args, evaluate2, ctx) {
  const value = evaluate2(args[0], ctx);
  const min = evaluate2(args[1], ctx);
  const max = evaluate2(args[2], ctx);
  return typeof value === "number" && value >= min && value <= max;
}
function evalValidatePattern(args, evaluate2, ctx) {
  const value = evaluate2(args[0], ctx);
  const pattern = evaluate2(args[1], ctx);
  if (typeof value !== "string") return false;
  try {
    const regex = new RegExp(pattern);
    return regex.test(value);
  } catch {
    return false;
  }
}
function evalValidateOneOf(args, evaluate2, ctx) {
  const value = evaluate2(args[0], ctx);
  const options = evaluate2(args[1], ctx);
  return (options ?? []).includes(value);
}
function evalValidateNoneOf(args, evaluate2, ctx) {
  const value = evaluate2(args[0], ctx);
  const options = evaluate2(args[1], ctx);
  return !(options ?? []).includes(value);
}
function evalValidateEquals(args, evaluate2, ctx) {
  const a = evaluate2(args[0], ctx);
  const b = evaluate2(args[1], ctx);
  function deepEqual2(x, y) {
    if (x === y) return true;
    if (typeof x !== typeof y) return false;
    if (typeof x !== "object" || x === null || y === null) return false;
    if (Array.isArray(x) !== Array.isArray(y)) return false;
    const xKeys = Object.keys(x);
    const yKeys = Object.keys(y);
    if (xKeys.length !== yKeys.length) return false;
    for (const key2 of xKeys) {
      if (!deepEqual2(x[key2], y[key2])) {
        return false;
      }
    }
    return true;
  }
  return deepEqual2(a, b);
}
function evalValidateCheck(args, evaluate2, ctx) {
  const value = evaluate2(args[0], ctx);
  const rules = evaluate2(args[1], ctx);
  const errors = [];
  for (const [field, fieldRules] of Object.entries(rules ?? {})) {
    const fieldValue = value?.[field];
    for (const rule of fieldRules ?? []) {
      const [ruleName, ...ruleArgs] = rule;
      let isValid = true;
      switch (ruleName) {
        case "required":
          isValid = fieldValue !== null && fieldValue !== void 0 && fieldValue !== "";
          break;
        case "string":
          isValid = typeof fieldValue === "string";
          break;
        case "number":
          isValid = typeof fieldValue === "number" && !isNaN(fieldValue);
          break;
        case "boolean":
          isValid = typeof fieldValue === "boolean";
          break;
        case "email": {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          isValid = typeof fieldValue === "string" && emailRegex.test(fieldValue);
          break;
        }
        case "minLength":
          isValid = (typeof fieldValue === "string" || Array.isArray(fieldValue)) && fieldValue.length >= ruleArgs[0];
          break;
        case "maxLength":
          isValid = (typeof fieldValue === "string" || Array.isArray(fieldValue)) && fieldValue.length <= ruleArgs[0];
          break;
        case "min":
          isValid = typeof fieldValue === "number" && fieldValue >= ruleArgs[0];
          break;
        case "max":
          isValid = typeof fieldValue === "number" && fieldValue <= ruleArgs[0];
          break;
        case "range":
          isValid = typeof fieldValue === "number" && fieldValue >= ruleArgs[0] && fieldValue <= ruleArgs[1];
          break;
        case "pattern": {
          const regex = new RegExp(ruleArgs[0]);
          isValid = typeof fieldValue === "string" && regex.test(fieldValue);
          break;
        }
        case "oneOf":
          isValid = ruleArgs[0]?.includes(fieldValue);
          break;
        default:
          continue;
      }
      if (!isValid) {
        errors.push(`${field}: ${ruleName} validation failed`);
      }
    }
  }
  return { valid: errors.length === 0, errors };
}

// std/time.ts
var MS_PER_SECOND = 1e3;
var MS_PER_MINUTE = 60 * MS_PER_SECOND;
var MS_PER_HOUR = 60 * MS_PER_MINUTE;
var MS_PER_DAY = 24 * MS_PER_HOUR;
var MS_PER_WEEK = 7 * MS_PER_DAY;
var TIME_UNIT_ALIASES = {
  ms: "ms",
  millisecond: "ms",
  milliseconds: "ms",
  s: "second",
  second: "second",
  seconds: "second",
  m: "minute",
  minute: "minute",
  minutes: "minute",
  h: "hour",
  hour: "hour",
  hours: "hour",
  d: "day",
  day: "day",
  days: "day",
  w: "week",
  week: "week",
  weeks: "week",
  month: "month",
  months: "month",
  quarter: "quarter",
  quarters: "quarter",
  year: "year",
  years: "year"
};
var TIME_UNIT_VOCABULARY = "ms|millisecond(s), s|second(s), m|minute(s), h|hour(s), d|day(s), w|week(s), month(s), quarter(s), year(s)";
function parseTimeUnit(unit) {
  const resolved = TIME_UNIT_ALIASES[String(unit)];
  if (resolved === void 0) {
    throw new TypeError(
      `Type mismatch: expected time unit (${TIME_UNIT_VOCABULARY}), got ${String(unit)}`
    );
  }
  return resolved;
}
var FIXED_UNIT_MS = {
  ms: 1,
  second: MS_PER_SECOND,
  minute: MS_PER_MINUTE,
  hour: MS_PER_HOUR,
  day: MS_PER_DAY,
  week: MS_PER_WEEK
};
function daysInMonth(year, month) {
  return new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
}
function addCalendarMonths(ts, months) {
  const date = new Date(ts);
  const y = date.getUTCFullYear();
  const m = date.getUTCMonth();
  const total = y * 12 + m + months;
  const ny = Math.floor(total / 12);
  const nm = (total % 12 + 12) % 12;
  const nd = Math.min(date.getUTCDate(), daysInMonth(ny, nm));
  const shifted = new Date(ts);
  shifted.setUTCFullYear(ny, nm, nd);
  return shifted.getTime();
}
function shiftTime(ts, amount, unit) {
  const fixed = FIXED_UNIT_MS[unit];
  if (fixed !== void 0) return ts + amount * fixed;
  const monthsPer = unit === "year" ? 12 : unit === "quarter" ? 3 : 1;
  return addCalendarMonths(ts, Math.trunc(amount) * monthsPer);
}
function startOf(ts, unit) {
  switch (unit) {
    case "ms":
      return Math.floor(ts);
    case "second":
      return Math.floor(ts / MS_PER_SECOND) * MS_PER_SECOND;
    case "minute":
      return Math.floor(ts / MS_PER_MINUTE) * MS_PER_MINUTE;
    case "hour":
      return Math.floor(ts / MS_PER_HOUR) * MS_PER_HOUR;
    case "day":
      return Math.floor(ts / MS_PER_DAY) * MS_PER_DAY;
    case "week": {
      const days = Math.floor(ts / MS_PER_DAY);
      const dow = ((days + 3) % 7 + 7) % 7;
      return (days - dow) * MS_PER_DAY;
    }
    case "month": {
      const d = new Date(ts);
      return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1);
    }
    case "quarter": {
      const d = new Date(ts);
      return Date.UTC(d.getUTCFullYear(), Math.floor(d.getUTCMonth() / 3) * 3, 1);
    }
    case "year":
      return Date.UTC(new Date(ts).getUTCFullYear(), 0, 1);
  }
}
function wholeMonthsBetween(from, to) {
  const lo = new Date(Math.min(from, to));
  const hi = new Date(Math.max(from, to));
  let months = (hi.getUTCFullYear() - lo.getUTCFullYear()) * 12 + (hi.getUTCMonth() - lo.getUTCMonth());
  const loRest = [lo.getUTCDate(), lo.getTime() - startOf(lo.getTime(), "day")];
  const hiRest = [hi.getUTCDate(), hi.getTime() - startOf(hi.getTime(), "day")];
  if (hiRest[0] < loRest[0] || hiRest[0] === loRest[0] && hiRest[1] < loRest[1]) {
    months -= 1;
  }
  return to < from ? -months : months;
}
function evalTimeNow(ctx) {
  return ctx.now;
}
function evalTimeToday(ctx) {
  const now = new Date(ctx.now);
  now.setHours(0, 0, 0, 0);
  return now.getTime();
}
function evalTimeParse(args, evaluate2, ctx) {
  const str = evaluate2(args[0], ctx);
  const date = new Date(str);
  return date.getTime();
}
function evalTimeFormat(args, evaluate2, ctx) {
  const timestamp = evaluate2(args[0], ctx);
  const format = evaluate2(args[1], ctx);
  const date = new Date(timestamp);
  const tokens = {
    YYYY: String(date.getFullYear()),
    YY: String(date.getFullYear()).slice(-2),
    MM: String(date.getMonth() + 1).padStart(2, "0"),
    M: String(date.getMonth() + 1),
    DD: String(date.getDate()).padStart(2, "0"),
    D: String(date.getDate()),
    HH: String(date.getHours()).padStart(2, "0"),
    H: String(date.getHours()),
    mm: String(date.getMinutes()).padStart(2, "0"),
    m: String(date.getMinutes()),
    ss: String(date.getSeconds()).padStart(2, "0"),
    s: String(date.getSeconds()),
    ddd: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][date.getDay()],
    dddd: ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"][date.getDay()],
    MMM: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][date.getMonth()],
    MMMM: [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December"
    ][date.getMonth()]
  };
  const pattern = new RegExp(
    Object.keys(tokens).sort((a, b) => b.length - a.length).join("|"),
    "g"
  );
  return format.replace(pattern, (token) => tokens[token]);
}
function evalTimeYear(args, evaluate2, ctx) {
  const timestamp = evaluate2(args[0], ctx);
  return new Date(timestamp).getFullYear();
}
function evalTimeMonth(args, evaluate2, ctx) {
  const timestamp = evaluate2(args[0], ctx);
  return new Date(timestamp).getMonth() + 1;
}
function evalTimeDay(args, evaluate2, ctx) {
  const timestamp = evaluate2(args[0], ctx);
  return new Date(timestamp).getDate();
}
function evalTimeWeekday(args, evaluate2, ctx) {
  const timestamp = evaluate2(args[0], ctx);
  return new Date(timestamp).getDay();
}
function evalTimeHour(args, evaluate2, ctx) {
  const timestamp = evaluate2(args[0], ctx);
  return new Date(timestamp).getHours();
}
function evalTimeMinute(args, evaluate2, ctx) {
  const timestamp = evaluate2(args[0], ctx);
  return new Date(timestamp).getMinutes();
}
function evalTimeSecond(args, evaluate2, ctx) {
  const timestamp = evaluate2(args[0], ctx);
  return new Date(timestamp).getSeconds();
}
function evalTimeAdd(args, evaluate2, ctx) {
  const timestamp = evaluate2(args[0], ctx);
  const amount = evaluate2(args[1], ctx);
  const unit = parseTimeUnit(evaluate2(args[2], ctx));
  return shiftTime(timestamp, amount, unit);
}
function evalTimeSubtract(args, evaluate2, ctx) {
  const timestamp = evaluate2(args[0], ctx);
  const amount = evaluate2(args[1], ctx);
  const unit = parseTimeUnit(evaluate2(args[2], ctx));
  return shiftTime(timestamp, -amount, unit);
}
function evalTimeDiff(args, evaluate2, ctx) {
  const a = evaluate2(args[0], ctx);
  const b = evaluate2(args[1], ctx);
  const diffMs = a - b;
  if (args.length <= 2) return diffMs;
  const unit = parseTimeUnit(evaluate2(args[2], ctx));
  const fixed = FIXED_UNIT_MS[unit];
  if (fixed !== void 0) return Math.trunc(diffMs / fixed);
  const months = wholeMonthsBetween(b, a);
  return unit === "year" ? Math.trunc(months / 12) : unit === "quarter" ? Math.trunc(months / 3) : months;
}
function evalTimeStartOf(args, evaluate2, ctx) {
  const timestamp = evaluate2(args[0], ctx);
  const unit = parseTimeUnit(evaluate2(args[1], ctx));
  return startOf(timestamp, unit);
}
function evalTimeEndOf(args, evaluate2, ctx) {
  const timestamp = evaluate2(args[0], ctx);
  const unit = parseTimeUnit(evaluate2(args[1], ctx));
  if (unit === "ms") return Math.floor(timestamp);
  return shiftTime(startOf(timestamp, unit), 1, unit) - 1;
}
function evalTimeIsBefore(args, evaluate2, ctx) {
  const a = evaluate2(args[0], ctx);
  const b = evaluate2(args[1], ctx);
  return a < b;
}
function evalTimeIsAfter(args, evaluate2, ctx) {
  const a = evaluate2(args[0], ctx);
  const b = evaluate2(args[1], ctx);
  return a > b;
}
function evalTimeIsBetween(args, evaluate2, ctx) {
  const date = evaluate2(args[0], ctx);
  const start = evaluate2(args[1], ctx);
  const end = evaluate2(args[2], ctx);
  return date >= start && date <= end;
}
function evalTimeIsSame(args, evaluate2, ctx) {
  const a = evaluate2(args[0], ctx);
  const b = evaluate2(args[1], ctx);
  if (args.length <= 2) {
    return a === b;
  }
  const unit = parseTimeUnit(evaluate2(args[2], ctx));
  return startOf(a, unit) === startOf(b, unit);
}
function evalTimeIsPast(args, evaluate2, ctx) {
  const timestamp = evaluate2(args[0], ctx);
  return timestamp < ctx.now;
}
function evalTimeIsFuture(args, evaluate2, ctx) {
  const timestamp = evaluate2(args[0], ctx);
  return timestamp > ctx.now;
}
function evalTimeIsToday(args, evaluate2, ctx) {
  const timestamp = evaluate2(args[0], ctx);
  const date = new Date(timestamp);
  const today = new Date(ctx.now);
  return date.getFullYear() === today.getFullYear() && date.getMonth() === today.getMonth() && date.getDate() === today.getDate();
}
function evalTimeRelative(args, evaluate2, ctx) {
  const timestamp = evaluate2(args[0], ctx);
  const now = ctx.now;
  const diff = timestamp - now;
  const absDiff = Math.abs(diff);
  const isPast = diff < 0;
  let value;
  let unit;
  if (absDiff < MS_PER_MINUTE) {
    return "just now";
  } else if (absDiff < MS_PER_HOUR) {
    value = Math.round(absDiff / MS_PER_MINUTE);
    unit = value === 1 ? "minute" : "minutes";
  } else if (absDiff < MS_PER_DAY) {
    value = Math.round(absDiff / MS_PER_HOUR);
    unit = value === 1 ? "hour" : "hours";
  } else if (absDiff < MS_PER_WEEK) {
    value = Math.round(absDiff / MS_PER_DAY);
    unit = value === 1 ? "day" : "days";
  } else if (absDiff < MS_PER_DAY * 30) {
    value = Math.round(absDiff / MS_PER_WEEK);
    unit = value === 1 ? "week" : "weeks";
  } else if (absDiff < MS_PER_DAY * 365) {
    value = Math.round(absDiff / (MS_PER_DAY * 30));
    unit = value === 1 ? "month" : "months";
  } else {
    value = Math.round(absDiff / (MS_PER_DAY * 365));
    unit = value === 1 ? "year" : "years";
  }
  return isPast ? `${value} ${unit} ago` : `in ${value} ${unit}`;
}
function evalTimeDuration(args, evaluate2, ctx) {
  let ms = evaluate2(args[0], ctx);
  if (ms < 0) ms = -ms;
  const parts = [];
  if (ms >= MS_PER_DAY) {
    const days = Math.floor(ms / MS_PER_DAY);
    parts.push(`${days}d`);
    ms %= MS_PER_DAY;
  }
  if (ms >= MS_PER_HOUR) {
    const hours = Math.floor(ms / MS_PER_HOUR);
    parts.push(`${hours}h`);
    ms %= MS_PER_HOUR;
  }
  if (ms >= MS_PER_MINUTE) {
    const minutes = Math.floor(ms / MS_PER_MINUTE);
    parts.push(`${minutes}m`);
    ms %= MS_PER_MINUTE;
  }
  if (ms >= MS_PER_SECOND && parts.length < 2) {
    const seconds = Math.floor(ms / MS_PER_SECOND);
    parts.push(`${seconds}s`);
  }
  return parts.length > 0 ? parts.join(" ") : "0s";
}

// std/format.ts
function evalFormatNumber(args, evaluate2, ctx) {
  const n = evaluate2(args[0], ctx);
  const opts = args.length > 1 ? evaluate2(args[1], ctx) : {};
  const locale = opts?.locale ?? "en-US";
  const options = {};
  if (opts?.decimals !== void 0) {
    options.minimumFractionDigits = opts.decimals;
    options.maximumFractionDigits = opts.decimals;
  }
  return new Intl.NumberFormat(locale, options).format(n);
}
function evalFormatCurrency(args, evaluate2, ctx) {
  const n = evaluate2(args[0], ctx);
  const currency = evaluate2(args[1], ctx);
  const locale = args.length > 2 ? evaluate2(args[2], ctx) : "en-US";
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency
  }).format(n);
}
function evalFormatPercent(args, evaluate2, ctx) {
  const n = evaluate2(args[0], ctx);
  const decimals = args.length > 1 ? evaluate2(args[1], ctx) : 0;
  return new Intl.NumberFormat("en-US", {
    style: "percent",
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  }).format(n);
}
function evalFormatBytes(args, evaluate2, ctx) {
  const bytes = evaluate2(args[0], ctx);
  if (bytes === 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB", "PB"];
  const k = 1024;
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  const value = bytes / Math.pow(k, i);
  if (i === 0) {
    return `${bytes} B`;
  }
  let formatted;
  if (value >= 10) {
    formatted = String(Math.round(value));
  } else if (Number.isInteger(value)) {
    formatted = String(value);
  } else {
    formatted = value.toFixed(1);
  }
  return `${formatted} ${units[i]}`;
}
function evalFormatOrdinal(args, evaluate2, ctx) {
  const n = evaluate2(args[0], ctx);
  const absN = Math.abs(n);
  const lastDigit = absN % 10;
  const lastTwoDigits = absN % 100;
  let suffix;
  if (lastTwoDigits >= 11 && lastTwoDigits <= 13) {
    suffix = "th";
  } else if (lastDigit === 1) {
    suffix = "st";
  } else if (lastDigit === 2) {
    suffix = "nd";
  } else if (lastDigit === 3) {
    suffix = "rd";
  } else {
    suffix = "th";
  }
  return `${n}${suffix}`;
}
function evalFormatPlural(args, evaluate2, ctx) {
  const n = evaluate2(args[0], ctx);
  const singular = evaluate2(args[1], ctx);
  const plural = evaluate2(args[2], ctx);
  return `${n} ${Math.abs(n) === 1 ? singular : plural}`;
}
function evalFormatList(args, evaluate2, ctx) {
  const arr = evaluate2(args[0], ctx);
  const style = args.length > 1 ? evaluate2(args[1], ctx) : "and";
  if (!arr || arr.length === 0) return "";
  if (arr.length === 1) return arr[0];
  if (arr.length === 2) return `${arr[0]} ${style} ${arr[1]}`;
  const last = arr[arr.length - 1];
  const rest = arr.slice(0, -1);
  return `${rest.join(", ")}, ${style} ${last}`;
}
function evalFormatPhone(args, evaluate2, ctx) {
  const str = evaluate2(args[0], ctx);
  const format = args.length > 1 ? evaluate2(args[1], ctx) : "US";
  const digits = str?.replace(/\D/g, "") ?? "";
  if (format === "US" && digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  if (format === "US" && digits.length === 11 && digits[0] === "1") {
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  if (digits.length >= 10) {
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return str;
}
function evalFormatCreditCard(args, evaluate2, ctx) {
  const str = evaluate2(args[0], ctx);
  const digits = str?.replace(/\D/g, "") ?? "";
  if (digits.length < 4) return str;
  const lastFour = digits.slice(-4);
  const masked = "\u2022".repeat(digits.length - 4);
  const combined = masked + lastFour;
  return combined.match(/.{1,4}/g)?.join(" ") ?? combined;
}

// std/i18n.ts
function evalI18nT(args, evaluate2, ctx) {
  const key2 = String(evaluate2(args[0], ctx));
  const message = ctx.messages?.[key2];
  if (message === void 0) {
    throw new Error(`i18n/t: no \`${ctx.locale ?? "unset"}\` message for \`${key2}\``);
  }
  if (args.length < 2) return message;
  const params = evaluate2(args[1], ctx);
  if (typeof params !== "object" || params === null || Array.isArray(params)) return message;
  let out = message;
  for (const [name, value] of Object.entries(params)) {
    out = out.split(`{{${name}}}`).join(String(value));
  }
  return out;
}

// std/async.ts
var debounceTimers = /* @__PURE__ */ new Map();
var throttleTimestamps = /* @__PURE__ */ new Map();
var intervalTimers = /* @__PURE__ */ new Map();
var intervalIdCounter = 0;
async function evalAsyncDelay(args, evaluate2, ctx) {
  const ms = evaluate2(args[0], ctx);
  await new Promise((resolve) => setTimeout(resolve, ms));
  if (args.length >= 2 && args[1] !== void 0) {
    return evaluate2(args[1], ctx);
  }
  return void 0;
}
function evalAsyncInterval(args, evaluate2, ctx) {
  const ms = evaluate2(args[0], ctx);
  const effect = args[1];
  const id = `interval_${++intervalIdCounter}`;
  const timer = setInterval(() => {
    try {
      evaluate2(effect, ctx);
    } catch {
    }
  }, ms);
  intervalTimers.set(id, timer);
  return id;
}
async function evalAsyncTimeout(args, evaluate2, ctx) {
  const effect = args[0];
  const ms = evaluate2(args[1], ctx);
  return Promise.race([
    Promise.resolve().then(() => evaluate2(effect, ctx)),
    new Promise(
      (_, reject) => setTimeout(() => reject(new Error("Timeout exceeded")), ms)
    )
  ]);
}
function evalAsyncDebounce(args, evaluate2, ctx) {
  const event = evaluate2(args[0], ctx);
  const ms = evaluate2(args[1], ctx);
  const existingTimer = debounceTimers.get(event);
  if (existingTimer) {
    clearTimeout(existingTimer);
  }
  const timer = setTimeout(() => {
    debounceTimers.delete(event);
    ctx.emit?.(event);
  }, ms);
  debounceTimers.set(event, timer);
}
function evalAsyncThrottle(args, evaluate2, ctx) {
  const event = evaluate2(args[0], ctx);
  const ms = evaluate2(args[1], ctx);
  const now = ctx.now;
  const lastTimestamp = throttleTimestamps.get(event) ?? 0;
  if (now - lastTimestamp >= ms) {
    throttleTimestamps.set(event, now);
    ctx.emit?.(event);
  }
}
async function evalAsyncRetry(args, evaluate2, ctx) {
  const effect = args[0];
  const opts = evaluate2(args[1], ctx);
  const attempts = opts?.attempts ?? 3;
  const backoff = opts?.backoff ?? "exponential";
  const baseDelay = opts?.baseDelay ?? 1e3;
  let lastError;
  for (let i = 0; i < attempts; i++) {
    try {
      return await Promise.resolve().then(() => evaluate2(effect, ctx));
    } catch (error) {
      lastError = error;
      if (i < attempts - 1) {
        let delay;
        switch (backoff) {
          case "fixed":
            delay = baseDelay;
            break;
          case "linear":
            delay = baseDelay * (i + 1);
            break;
          case "exponential":
          default:
            delay = baseDelay * Math.pow(2, i);
            break;
        }
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    }
  }
  throw lastError;
}
async function evalAsyncRace(args, evaluate2, ctx) {
  const promises = args.map(
    (effect) => Promise.resolve().then(() => evaluate2(effect, ctx))
  );
  return Promise.race(promises);
}
async function evalAsyncAll(args, evaluate2, ctx) {
  const promises = args.map(
    (effect) => Promise.resolve().then(() => evaluate2(effect, ctx))
  );
  return Promise.all(promises);
}
async function evalAsyncSequence(args, evaluate2, ctx) {
  const results = [];
  for (const effect of args) {
    const result = await Promise.resolve().then(() => evaluate2(effect, ctx));
    results.push(result);
  }
  return results;
}

// std/prob.ts
function mulberry32(seed) {
  let t = seed.state += 1831565813;
  t = Math.imul(t ^ t >>> 15, t | 1);
  t ^= t + Math.imul(t ^ t >>> 7, t | 61);
  return ((t ^ t >>> 14) >>> 0) / 4294967296;
}
function getRandom(ctx) {
  if (ctx._probSeed) {
    return mulberry32(ctx._probSeed);
  }
  return Math.random();
}
function gammaVariate(shape, ctx) {
  if (shape < 1) {
    const u = getRandom(ctx);
    return gammaVariate(shape + 1, ctx) * Math.pow(u, 1 / shape);
  }
  const d = shape - 1 / 3;
  const c = 1 / Math.sqrt(9 * d);
  for (; ; ) {
    let x;
    let v;
    do {
      x = gaussianStandard(ctx);
      v = 1 + c * x;
    } while (v <= 0);
    v = v * v * v;
    const u = getRandom(ctx);
    if (u < 1 - 0.0331 * (x * x) * (x * x)) {
      return d * v;
    }
    if (Math.log(u) < 0.5 * x * x + d * (1 - v + Math.log(v))) {
      return d * v;
    }
  }
}
function gaussianStandard(ctx) {
  const u1 = getRandom(ctx);
  const u2 = getRandom(ctx);
  return Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2);
}
function evalProbSeed(args, evaluate2, ctx) {
  const n = evaluate2(args[0], ctx);
  ctx._probSeed = { state: n | 0 };
}
function evalProbFlip(args, evaluate2, ctx) {
  const p2 = evaluate2(args[0], ctx);
  return getRandom(ctx) < p2;
}
function evalProbGaussian(args, evaluate2, ctx) {
  const mu = evaluate2(args[0], ctx);
  const sigma = evaluate2(args[1], ctx);
  return mu + sigma * gaussianStandard(ctx);
}
function evalProbUniform(args, evaluate2, ctx) {
  const lo = evaluate2(args[0], ctx);
  const hi = evaluate2(args[1], ctx);
  return lo + getRandom(ctx) * (hi - lo);
}
function evalProbBeta(args, evaluate2, ctx) {
  const alpha = evaluate2(args[0], ctx);
  const beta = evaluate2(args[1], ctx);
  const x = gammaVariate(alpha, ctx);
  const y = gammaVariate(beta, ctx);
  return x / (x + y);
}
function evalProbCategorical(args, evaluate2, ctx) {
  const items = evaluate2(args[0], ctx);
  const weights = evaluate2(args[1], ctx);
  const totalWeight = weights.reduce((sum, w) => sum + w, 0);
  let r = getRandom(ctx) * totalWeight;
  for (let i = 0; i < items.length; i++) {
    r -= weights[i];
    if (r <= 0) return items[i];
  }
  return items[items.length - 1];
}
function evalProbPoisson(args, evaluate2, ctx) {
  const lambda = evaluate2(args[0], ctx);
  const limit = Math.exp(-lambda);
  let k = 0;
  let p2 = 1;
  do {
    k++;
    p2 *= getRandom(ctx);
  } while (p2 > limit);
  return k - 1;
}
function evalProbCondition(args, evaluate2, ctx) {
  const predicate = evaluate2(args[0], ctx);
  if (!predicate) {
    ctx._probRejected = true;
  }
}
function evalProbSample(args, evaluate2, ctx) {
  const n = evaluate2(args[0], ctx);
  const exprAst = args[1];
  const results = [];
  for (let i = 0; i < n; i++) {
    results.push(evaluate2(exprAst, ctx));
  }
  return results;
}
function evalProbPosterior(args, evaluate2, ctx) {
  const modelAst = args[0];
  const evidenceAst = args[1];
  const queryAst = args[2];
  const n = evaluate2(args[3], ctx);
  const results = [];
  for (let i = 0; i < n; i++) {
    const sampleEntity = { ...ctx.entity };
    const sampleCtx = {
      ...ctx,
      entity: sampleEntity,
      _probRejected: false,
      mutateEntity: (changes) => {
        Object.assign(sampleEntity, changes);
      }
    };
    evaluate2(modelAst, sampleCtx);
    if (sampleCtx._probRejected) continue;
    const evidenceResult = evaluate2(evidenceAst, sampleCtx);
    if (!evidenceResult) continue;
    results.push(evaluate2(queryAst, sampleCtx));
  }
  return results;
}
function evalProbInfer(args, evaluate2, ctx) {
  const modelAst = args[0];
  const evidenceAst = args[1];
  const queryAst = args[2];
  const n = evaluate2(args[3], ctx);
  const accepted = [];
  for (let i = 0; i < n; i++) {
    const sampleEntity = { ...ctx.entity };
    const sampleCtx = {
      ...ctx,
      entity: sampleEntity,
      _probRejected: false,
      mutateEntity: (changes) => {
        Object.assign(sampleEntity, changes);
      }
    };
    evaluate2(modelAst, sampleCtx);
    if (sampleCtx._probRejected) continue;
    const evidenceResult = evaluate2(evidenceAst, sampleCtx);
    if (!evidenceResult) continue;
    const queryVal = evaluate2(queryAst, sampleCtx);
    accepted.push(queryVal);
  }
  const count = accepted.length;
  if (count === 0) {
    return { mean: 0, variance: 0, samples: [], acceptRate: 0 };
  }
  const mean = accepted.reduce((s, v) => s + v, 0) / count;
  const variance = accepted.reduce((s, v) => s + (v - mean) ** 2, 0) / count;
  return {
    mean,
    variance,
    samples: accepted,
    acceptRate: count / n
  };
}
function evalProbExpectedValue(args, evaluate2, ctx) {
  const samples = evaluate2(args[0], ctx);
  if (samples.length === 0) return 0;
  return samples.reduce((s, v) => s + v, 0) / samples.length;
}
function evalProbVariance(args, evaluate2, ctx) {
  const samples = evaluate2(args[0], ctx);
  if (samples.length === 0) return 0;
  const mean = samples.reduce((s, v) => s + v, 0) / samples.length;
  return samples.reduce((s, v) => s + (v - mean) ** 2, 0) / samples.length;
}
function evalProbHistogram(args, evaluate2, ctx) {
  const samples = evaluate2(args[0], ctx);
  const bins = evaluate2(args[1], ctx);
  if (samples.length === 0) {
    return { binEdges: [], counts: [] };
  }
  const min = Math.min(...samples);
  const max = Math.max(...samples);
  const binWidth = (max - min) / bins || 1;
  const binEdges = [];
  const counts = new Array(bins).fill(0);
  for (let i = 0; i <= bins; i++) {
    binEdges.push(min + i * binWidth);
  }
  for (const s of samples) {
    let idx = Math.floor((s - min) / binWidth);
    if (idx >= bins) idx = bins - 1;
    counts[idx]++;
  }
  return { binEdges, counts };
}
function evalProbPercentile(args, evaluate2, ctx) {
  const samples = evaluate2(args[0], ctx);
  const p2 = evaluate2(args[1], ctx);
  if (samples.length === 0) return 0;
  const sorted = [...samples].sort((a, b) => a - b);
  const idx = p2 / 100 * (sorted.length - 1);
  const lo = Math.floor(idx);
  const hi = Math.ceil(idx);
  if (lo === hi) return sorted[lo];
  const frac = idx - lo;
  return sorted[lo] * (1 - frac) + sorted[hi] * frac;
}
function evalProbCredibleInterval(args, evaluate2, ctx) {
  const samples = evaluate2(args[0], ctx);
  const alpha = evaluate2(args[1], ctx);
  if (samples.length === 0) return [0, 0];
  const sorted = [...samples].sort((a, b) => a - b);
  const loIdx = Math.floor(alpha / 2 * (sorted.length - 1));
  const hiIdx = Math.ceil((1 - alpha / 2) * (sorted.length - 1));
  return [sorted[loIdx], sorted[hiIdx]];
}

// std/os.ts
function registerTrigger(ctx, type, config) {
  ctx.registerOsTrigger?.(type, config);
}
function evalOsWatchFiles(args, evaluate2, ctx) {
  const glob = evaluate2(args[0], ctx);
  const options = args[1] ? evaluate2(args[1], ctx) : {};
  registerTrigger(ctx, "watch-files", { glob, ...options });
}
function evalOsWatchProcess(args, evaluate2, ctx) {
  const name = evaluate2(args[0], ctx);
  const subcommand = args[1] ? evaluate2(args[1], ctx) : void 0;
  registerTrigger(ctx, "watch-process", { name, subcommand });
}
function evalOsWatchPort(args, evaluate2, ctx) {
  const port = evaluate2(args[0], ctx);
  const protocol = args[1] ? evaluate2(args[1], ctx) : "tcp";
  registerTrigger(ctx, "watch-port", { port, protocol });
}
function evalOsWatchHttp(args, evaluate2, ctx) {
  const urlPattern = evaluate2(args[0], ctx);
  const method = args[1] ? evaluate2(args[1], ctx) : void 0;
  registerTrigger(ctx, "watch-http", { urlPattern, method });
}
function evalOsWatchCron(args, evaluate2, ctx) {
  const expression = evaluate2(args[0], ctx);
  registerTrigger(ctx, "watch-cron", { expression });
}
function evalOsWatchSignal(args, evaluate2, ctx) {
  const signal = evaluate2(args[0], ctx);
  registerTrigger(ctx, "watch-signal", { signal });
}
function evalOsWatchEnv(args, evaluate2, ctx) {
  const variable = evaluate2(args[0], ctx);
  registerTrigger(ctx, "watch-env", { variable });
}
function evalOsDebounce(args, evaluate2, ctx) {
  const ms = evaluate2(args[0], ctx);
  const eventType = evaluate2(args[1], ctx);
  registerTrigger(ctx, "debounce", { ms, eventType });
}
function evalBehaviorApply(args, evaluate2, ctx) {
  const value = BehaviorValueSchema.safeParse(evaluate2(args[0], ctx));
  if (!value.success) {
    throw new Error("behavior/apply: the first argument must be a behavior value ({ behavior, trait } or { behavior, orbital })");
  }
  const overrides = JsonValueSchema.safeParse(evaluate2(args[1], ctx));
  if (!overrides.success) {
    throw new Error(`behavior/apply: the overrides must be plain data: ${overrides.error.message}`);
  }
  try {
    return applyBehaviorOverrides(value.data, overrides.data);
  } catch (error) {
    throw new Error(`behavior/apply: overrides do not fit this kind of value: ${error instanceof Error ? error.message : String(error)}`);
  }
}
function evalBehaviorRef(args, evaluate2, ctx) {
  const specifier = evaluate2(args[0], ctx);
  if (typeof specifier !== "string") throw new Error("behavior/ref: the specifier must be a string");
  return behaviorRef(specifier);
}
function evalQuasiquote(args, evaluate2, ctx) {
  const [body, ...holes] = args;
  if (typeof body !== "string") throw new Error("quasiquote: expected an encoded template body (string)");
  return instantiateQuasiquote(body, holes.map((h) => toProgramData(evaluate2(h, ctx))));
}
function evalUnquote() {
  throw new Error("unquote: legal only inside (quasiquote \u2026)");
}
function clampContract(tensor, contract) {
  if (!isTensorValue(tensor)) {
    throw new Error(
      `contract/clamp-output: expected a tensor (a number, or nested arrays of numbers), received ${describeTensorMismatch(tensor)}. Validate with contract/validate-output before clamping.`
    );
  }
  const ranges = contract.ranges ?? {};
  const size = tensorLastDimSize(tensor);
  let result = tensor;
  for (const [dimStr, bounds] of Object.entries(ranges)) {
    const dim = Number(dimStr);
    if (dim >= size) continue;
    const min = bounds.min ?? -Infinity;
    const max = bounds.max ?? Infinity;
    result = mapTensorLastDim(result, dim, (v) => Math.min(Math.max(v, min), max));
  }
  return result;
}
function evalContractValidateInput(args, evaluate2, ctx) {
  const tensor = evaluate2(args[0], ctx);
  const contract = evaluate2(args[1], ctx);
  return validateContract(tensor, contract);
}
function evalContractValidateOutput(args, evaluate2, ctx) {
  return evalContractValidateInput(args, evaluate2, ctx);
}
function evalContractClampOutput(args, evaluate2, ctx) {
  const tensor = evaluate2(args[0], ctx);
  const contract = evaluate2(args[1], ctx);
  return clampContract(tensor, contract);
}
function evalContractViolations(args, evaluate2, ctx) {
  const tensor = evaluate2(args[0], ctx);
  const contract = evaluate2(args[1], ctx);
  return validateContract(tensor, contract).violations;
}
function evalContractEntityToTensor(args, evaluate2, ctx) {
  const entity = evaluate2(args[0], ctx);
  const contract = evaluate2(args[1], ctx);
  const fields = contract.fields ?? [];
  return fields.map((f) => {
    const val = entity[contractFieldName(f)];
    return typeof val === "number" ? val : 0;
  });
}
function evalContractTensorToPayload(args, evaluate2, ctx) {
  const tensor = evaluate2(args[0], ctx);
  if (!isTensorValue(tensor)) {
    throw new Error(
      `contract/tensor-to-payload: expected a tensor (a number, or nested arrays of numbers), received ${describeTensorMismatch(tensor)}.`
    );
  }
  const contract = evaluate2(args[1], ctx);
  const fields = contract.fields ?? [];
  const values = Array.isArray(tensor) ? tensor : [tensor];
  const result = {};
  fields.forEach((f, i) => {
    if (i < values.length) result[contractFieldName(f)] = values[i];
  });
  return result;
}

// std/graph.ts
function tensorShape(t) {
  if (typeof t === "number") return [];
  if (t.length === 0) return [0];
  const first = t[0];
  if (typeof first === "number") return [t.length];
  return [t.length, ...tensorShape(first)];
}
function numNodes(g) {
  return g.x.length;
}
function subgraphData(g, mask) {
  const nodeMap = new Array(numNodes(g)).fill(-1);
  const x = [];
  let next = 0;
  for (let i = 0; i < numNodes(g); i++) {
    if (mask[i]) {
      nodeMap[i] = next++;
      x.push(g.x[i]);
    }
  }
  const [srcAll, dstAll] = g.edgeIndex;
  const src = [];
  const dst = [];
  for (let k = 0; k < srcAll.length; k++) {
    const s = srcAll[k];
    const d = dstAll[k];
    if (mask[s] && mask[d]) {
      src.push(nodeMap[s]);
      dst.push(nodeMap[d]);
    }
  }
  return { x, edgeIndex: [src, dst] };
}
function evalGraphFromEntities(args, evaluate2, ctx) {
  const entities = evaluate2(args[0], ctx);
  if (entities.length === 0) {
    return { x: [], edgeIndex: [[], []] };
  }
  const features = entities.map((e) => {
    const row = Object.values(e).filter((v) => typeof v === "number" || typeof v === "boolean").map((v) => typeof v === "boolean" ? v ? 1 : 0 : v);
    return row.length > 0 ? row : [0];
  });
  const maxLen = Math.max(...features.map((r) => r.length));
  const x = features.map((r) => [...r, ...new Array(maxLen - r.length).fill(0)]);
  return { x, edgeIndex: [[], []] };
}
function evalGraphFromAdjacency(args, evaluate2, ctx) {
  const adjacency = evaluate2(args[0], ctx);
  const features = evaluate2(args[1], ctx);
  const src = [];
  const dst = [];
  for (let i = 0; i < adjacency.length; i++) {
    const row = adjacency[i];
    for (let j = 0; j < row.length; j++) {
      if (row[j] !== 0) {
        src.push(i);
        dst.push(j);
      }
    }
  }
  return { x: features, edgeIndex: [src, dst] };
}
function evalGraphFromEdgeList(args, evaluate2, ctx) {
  const edges = evaluate2(args[0], ctx);
  const features = evaluate2(args[1], ctx);
  const shape = tensorShape(edges);
  let edgeIndex;
  if (shape.length === 2 && shape[1] === 2) {
    const rows = edges;
    edgeIndex = [rows.map((r) => r[0]), rows.map((r) => r[1])];
  } else {
    const pair = edges;
    edgeIndex = [pair[0] ?? [], pair[1] ?? []];
  }
  return { x: features, edgeIndex };
}
function evalGraphAddSelfLoops(args, evaluate2, ctx) {
  const g = evaluate2(args[0], ctx);
  const n = numNodes(g);
  const loop = Array.from({ length: n }, (_, i) => i);
  const [src, dst] = g.edgeIndex;
  return { x: g.x, edgeIndex: [[...src, ...loop], [...dst, ...loop]], edgeAttr: g.edgeAttr };
}
function evalGraphToUndirected(args, evaluate2, ctx) {
  const g = evaluate2(args[0], ctx);
  const [src, dst] = g.edgeIndex;
  return { x: g.x, edgeIndex: [[...src, ...dst], [...dst, ...src]], edgeAttr: g.edgeAttr };
}
function evalGraphSubgraph(args, evaluate2, ctx) {
  const g = evaluate2(args[0], ctx);
  const mask = evaluate2(args[1], ctx);
  return subgraphData(g, mask);
}
function evalGraphKHop(args, evaluate2, ctx) {
  const g = evaluate2(args[0], ctx);
  const node = evaluate2(args[1], ctx);
  const k = evaluate2(args[2], ctx);
  const [src, dst] = g.edgeIndex;
  const visited = /* @__PURE__ */ new Set([node]);
  let frontier = /* @__PURE__ */ new Set([node]);
  for (let hop = 0; hop < k; hop++) {
    const next = /* @__PURE__ */ new Set();
    for (const n of frontier) {
      for (let e = 0; e < src.length; e++) {
        if (src[e] === n && !visited.has(dst[e])) next.add(dst[e]);
        if (dst[e] === n && !visited.has(src[e])) next.add(src[e]);
      }
    }
    for (const v of next) visited.add(v);
    frontier = next;
  }
  const mask = new Array(numNodes(g)).fill(false);
  for (const n of visited) mask[n] = true;
  return subgraphData(g, mask);
}
function evalGraphNodeFeatures(args, evaluate2, ctx) {
  return evaluate2(args[0], ctx).x;
}
function evalGraphEdgeIndex(args, evaluate2, ctx) {
  return evaluate2(args[0], ctx).edgeIndex;
}
function evalGraphEdgeFeatures(args, evaluate2, ctx) {
  return evaluate2(args[0], ctx).edgeAttr;
}
function evalGraphNumNodes(args, evaluate2, ctx) {
  return numNodes(evaluate2(args[0], ctx));
}
function evalGraphNumEdges(args, evaluate2, ctx) {
  return evaluate2(args[0], ctx).edgeIndex[0].length;
}
function evalGraphDegree(args, evaluate2, ctx) {
  const g = evaluate2(args[0], ctx);
  const deg = new Array(numNodes(g)).fill(0);
  for (const s of g.edgeIndex[0]) deg[s]++;
  return deg;
}
function evalGraphBatch(args, evaluate2, ctx) {
  const graphs = evaluate2(args[0], ctx);
  if (graphs.length === 0) return { x: [], edgeIndex: [[], []] };
  const x = [];
  const src = [];
  const dst = [];
  let offset = 0;
  for (const g of graphs) {
    x.push(...g.x);
    src.push(...g.edgeIndex[0].map((s) => s + offset));
    dst.push(...g.edgeIndex[1].map((d) => d + offset));
    offset += numNodes(g);
  }
  return { x, edgeIndex: [src, dst] };
}

// std/data.ts
function isPlainRecord(v) {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}
function datasetGetItem(dataset, idx) {
  const item = dataset.data[idx];
  if (isPlainRecord(item)) {
    const x = item.input ?? item.observation ?? [];
    const y = item.target ?? item.output ?? [];
    return [x, y];
  }
  if (Array.isArray(item)) {
    return [item, item];
  }
  return [item, item];
}
function seededRandom(ctx) {
  if (ctx._probSeed) {
    const seed = ctx._probSeed;
    let t = seed.state += 1831565813;
    t = Math.imul(t ^ t >>> 15, t | 1);
    t ^= t + Math.imul(t ^ t >>> 7, t | 61);
    return ((t ^ t >>> 14) >>> 0) / 4294967296;
  }
  return Math.random();
}
function seededGaussian(ctx) {
  const u1 = seededRandom(ctx);
  const u2 = seededRandom(ctx);
  return Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2);
}
function shuffleInPlace(arr, ctx) {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(seededRandom(ctx) * (i + 1));
    const tmp = arr[i];
    arr[i] = arr[j];
    arr[j] = tmp;
  }
}
function tensorShape2(t) {
  if (typeof t === "number") return [];
  if (t.length === 0) return [0];
  const first = t[0];
  if (typeof first === "number") return [t.length];
  return [t.length, ...tensorShape2(first)];
}
function rank(t) {
  return tensorShape2(t).length;
}
function sampleMean(vals) {
  return vals.reduce((s, v) => s + v, 0) / vals.length;
}
function sampleStd(vals, mean) {
  if (vals.length <= 1) return 0;
  const variance = vals.reduce((s, v) => s + (v - mean) ** 2, 0) / (vals.length - 1);
  return Math.sqrt(variance);
}
function normalizeZScore(t) {
  if (rank(t) <= 1) {
    const vals = t;
    const mean = sampleMean(vals);
    const std = Math.max(sampleStd(vals, mean), 1e-8);
    return vals.map((v) => (v - mean) / std);
  }
  const rows = t;
  const cols = rows[0].length;
  const means = [];
  const stds = [];
  for (let c = 0; c < cols; c++) {
    const col = rows.map((r) => r[c]);
    const m = sampleMean(col);
    means.push(m);
    stds.push(Math.max(sampleStd(col, m), 1e-8));
  }
  return rows.map((r) => r.map((v, c) => (v - means[c]) / stds[c]));
}
function normalizeMinMax(t) {
  if (rank(t) <= 1) {
    const vals = t;
    const mn = Math.min(...vals);
    const mx = Math.max(...vals);
    const denom = Math.max(mx - mn, 1e-8);
    return vals.map((v) => (v - mn) / denom);
  }
  const rows = t;
  const cols = rows[0].length;
  const mins = [];
  const maxs = [];
  for (let c = 0; c < cols; c++) {
    const col = rows.map((r) => r[c]);
    mins.push(Math.min(...col));
    maxs.push(Math.max(...col));
  }
  return rows.map((r) => r.map((v, c) => (v - mins[c]) / Math.max(maxs[c] - mins[c], 1e-8)));
}
function mapTensor(t, fn) {
  if (typeof t === "number") return fn(t);
  return t.map((sub) => mapTensor(sub, fn));
}
function padOrTruncateLastDim(t, targetLen, padValue) {
  if (typeof t === "number") return t;
  if (t.length === 0 || typeof t[0] === "number") {
    const row = t;
    if (row.length < targetLen) {
      return [...row, ...new Array(targetLen - row.length).fill(padValue)];
    }
    return row.slice(0, targetLen);
  }
  return t.map((sub) => padOrTruncateLastDim(sub, targetLen, padValue));
}
function evalDataDataset(args, evaluate2, ctx) {
  const data = evaluate2(args[0], ctx);
  const config = evaluate2(args[1], ctx);
  return { data: Array.isArray(data) ? data : [], config };
}
function evalDataDataloader(args, evaluate2, ctx) {
  const dataset = evaluate2(args[0], ctx);
  const config = evaluate2(args[1], ctx);
  const batchSize = config.batchSize ?? config.batch_size ?? 32;
  const shuffle = config.shuffle ?? true;
  const indices = Array.from({ length: dataset.data.length }, (_, i) => i);
  if (shuffle) shuffleInPlace(indices, ctx);
  const batches = [];
  for (let i = 0; i < indices.length; i += batchSize) {
    const chunk = indices.slice(i, i + batchSize);
    const x = [];
    const y = [];
    for (const idx of chunk) {
      const [xi, yi] = datasetGetItem(dataset, idx);
      x.push(xi);
      y.push(yi);
    }
    batches.push({ x, y });
  }
  return { batches, batchSize, numBatches: batches.length };
}
function evalDataSplit(args, evaluate2, ctx) {
  const dataset = evaluate2(args[0], ctx);
  const config = evaluate2(args[1], ctx);
  const ratio = config.trainRatio ?? config.train_ratio ?? 0.8;
  const trainSize = Math.trunc(dataset.data.length * ratio);
  const indices = Array.from({ length: dataset.data.length }, (_, i) => i);
  shuffleInPlace(indices, ctx);
  const trainIdx = indices.slice(0, trainSize);
  const testIdx = indices.slice(trainSize);
  return [
    { data: trainIdx.map((i) => dataset.data[i]), config: dataset.config },
    { data: testIdx.map((i) => dataset.data[i]), config: dataset.config }
  ];
}
function evalDataNormalize(args, evaluate2, ctx) {
  const data = evaluate2(args[0], ctx);
  const config = evaluate2(args[1], ctx);
  const method = config.method ?? "zscore";
  if (method === "zscore") return normalizeZScore(data);
  if (method === "minmax") return normalizeMinMax(data);
  return data;
}
function evalDataAugment(args, evaluate2, ctx) {
  const data = evaluate2(args[0], ctx);
  const config = evaluate2(args[1], ctx);
  const noiseScale = config.noiseScale ?? config.noise_scale ?? 0.01;
  return mapTensor(data, (v) => v + seededGaussian(ctx) * noiseScale);
}
function evalDataTokenize(args, evaluate2, ctx) {
  const text = evaluate2(args[0], ctx);
  const config = evaluate2(args[1], ctx);
  const mode = config.mode ?? "char";
  const textStr = String(text);
  if (mode === "word") {
    const vocab = config.vocab ?? {};
    return textStr.split(/\s+/).filter((w) => w.length > 0).map((w) => vocab[w] ?? 0);
  }
  return Array.from(textStr).map((c) => c.codePointAt(0) ?? 0);
}
function evalDataPad(args, evaluate2, ctx) {
  const data = evaluate2(args[0], ctx);
  const config = evaluate2(args[1], ctx);
  const currentLen = (() => {
    const shape = tensorShape2(data);
    return shape.length === 0 ? 0 : shape[shape.length - 1];
  })();
  const targetLen = config.length ?? config.targetLength ?? currentLen;
  const padValue = config.padValue ?? config.pad_value ?? 0;
  return padOrTruncateLastDim(data, targetLen, padValue);
}

// std/llm.ts
function evalLlmGenerate(args, evaluate2, ctx) {
  if (!ctx.llm) return null;
  const prompt = evaluate2(args[0], ctx);
  const options = args.length > 1 ? evaluate2(args[1], ctx) : void 0;
  return ctx.llm.generate(prompt, options);
}
function evalLlmCallTools(args, evaluate2, ctx) {
  if (!ctx.llm) return null;
  const messages = evaluate2(args[0], ctx);
  const tools = evaluate2(args[1], ctx);
  return ctx.llm.callTools(messages, tools);
}
function evalLlmEmbed(args, evaluate2, ctx) {
  if (!ctx.llm) return null;
  const texts = evaluate2(args[0], ctx);
  return ctx.llm.embed(texts);
}
function evalLlmTokenCount(_args, _evaluate, ctx) {
  if (!ctx.llm) return 0;
  return ctx.llm.tokenCount();
}
function evalLlmSwitch(args, evaluate2, ctx) {
  if (!ctx.llm) return;
  const provider = evaluate2(args[0], ctx);
  const model = args.length > 1 ? evaluate2(args[1], ctx) : void 0;
  ctx.llm.switchProvider(provider, model);
}
function evalLlmCompact(args, evaluate2, ctx) {
  if (!ctx.llm) return null;
  const strategy = args.length > 0 ? evaluate2(args[0], ctx) : void 0;
  return ctx.llm.compact(strategy);
}
function isOrbital(value) {
  return OrbitalZodSchema.safeParse(value).success;
}
function isOrbitalSchemaValue(value) {
  return safeParseOrbitalSchema(value).success;
}

// std/workspace.ts
function evalWorkspaceReadOrbital(args, evaluate2, ctx) {
  if (!ctx.workspace) return null;
  const name = evaluate2(args[0], ctx);
  return ctx.workspace.readOrbital(name);
}
function evalWorkspaceWriteOrbital(args, evaluate2, ctx) {
  if (!ctx.workspace) return null;
  const name = evaluate2(args[0], ctx);
  const content = evaluate2(args[1], ctx);
  if (!isOrbital(content)) {
    throw new Error(`workspace/write-orbital value for "${name}" is not a valid orbital definition`);
  }
  return ctx.workspace.writeOrbital(name, content);
}
function evalWorkspaceReadFile(args, evaluate2, ctx) {
  if (!ctx.workspace) return null;
  const path = evaluate2(args[0], ctx);
  return ctx.workspace.readFile(path);
}
function evalWorkspaceWriteFile(args, evaluate2, ctx) {
  if (!ctx.workspace) return null;
  const path = evaluate2(args[0], ctx);
  const content = evaluate2(args[1], ctx);
  return ctx.workspace.writeFile(path, content);
}
function evalWorkspaceExists(args, evaluate2, ctx) {
  if (!ctx.workspace) return false;
  const path = evaluate2(args[0], ctx);
  return ctx.workspace.exists(path);
}
function evalWorkspaceListOrbitals(_args, _evaluate, ctx) {
  if (!ctx.workspace) return [];
  return ctx.workspace.listOrbitals();
}
function evalWorkspaceReadSchema(_args, _evaluate, ctx) {
  if (!ctx.workspace) return null;
  return ctx.workspace.readSchema();
}
function evalWorkspaceWriteSchema(args, evaluate2, ctx) {
  if (!ctx.workspace) return null;
  const schema = evaluate2(args[0], ctx);
  if (!isOrbitalSchemaValue(schema)) {
    throw new Error("workspace/write-schema value is not a valid orbital schema");
  }
  return ctx.workspace.writeSchema(schema);
}
function evalWorkspaceReadPlan(_args, _evaluate, ctx) {
  if (!ctx.workspace) return null;
  return ctx.workspace.readPlan();
}
function evalWorkspaceWritePlan(args, evaluate2, ctx) {
  if (!ctx.workspace) return null;
  const plan = evaluate2(args[0], ctx);
  if (!isPlanSnapshot(plan)) {
    throw new Error("workspace/write-plan value is not a valid plan snapshot (schemaVersion 1 envelope)");
  }
  return ctx.workspace.writePlan(plan);
}
function evalWorkspaceArchiveOrbital(args, evaluate2, ctx) {
  if (!ctx.workspace) return null;
  const name = evaluate2(args[0], ctx);
  return ctx.workspace.archiveOrbital(name);
}

// std/session.ts
function evalSessionReadSpec(args, evaluate2, ctx) {
  if (!ctx.session) return null;
  const orbitalName = evaluate2(args[0], ctx);
  return ctx.session.readSpec(orbitalName);
}
function evalSessionWriteSpec(args, evaluate2, ctx) {
  if (!ctx.session) return null;
  const orbitalName = evaluate2(args[0], ctx);
  const spec = evaluate2(args[1], ctx);
  if (!isOrbital(spec)) {
    throw new Error(`session/write-spec value for "${orbitalName}" is not a valid orbital definition`);
  }
  return ctx.session.writeSpec(orbitalName, spec);
}
function evalSessionReadMemory(args, evaluate2, ctx) {
  if (!ctx.session) return null;
  const orbitalName = evaluate2(args[0], ctx);
  return ctx.session.readMemory(orbitalName);
}
function evalSessionWriteMemory(args, evaluate2, ctx) {
  if (!ctx.session) return null;
  const orbitalName = evaluate2(args[0], ctx);
  const memory = evaluate2(args[1], ctx);
  return ctx.session.writeMemory(orbitalName, memory);
}
function evalSessionReadHistory(args, evaluate2, ctx) {
  if (!ctx.session) return null;
  const orbitalName = evaluate2(args[0], ctx);
  return ctx.session.readHistory(orbitalName);
}
function evalSessionAppendHistory(args, evaluate2, ctx) {
  if (!ctx.session) return null;
  const orbitalName = evaluate2(args[0], ctx);
  const entry = evaluate2(args[1], ctx);
  if (!isSessionHistoryEntry(entry)) {
    throw new Error(`session/append-history entry for "${orbitalName}" must have role/content/timestamp`);
  }
  return ctx.session.appendHistory(orbitalName, entry);
}
function evalSessionReadErrors(args, evaluate2, ctx) {
  if (!ctx.session) return null;
  const orbitalName = evaluate2(args[0], ctx);
  return ctx.session.readErrors(orbitalName);
}
function evalSessionWriteErrors(args, evaluate2, ctx) {
  if (!ctx.session) return null;
  const orbitalName = evaluate2(args[0], ctx);
  const errors = evaluate2(args[1], ctx);
  return ctx.session.writeErrors(orbitalName, errors);
}
function evalSessionReadAnalysis(args, evaluate2, ctx) {
  if (!ctx.session) return null;
  const orbitalName = evaluate2(args[0], ctx);
  return ctx.session.readAnalysis(orbitalName);
}

// std/memory.ts
function evalMemoryRecall(args, evaluate2, ctx) {
  if (!ctx.memory) return [];
  const query = evaluate2(args[0], ctx);
  const limit = args.length > 1 ? evaluate2(args[1], ctx) : void 0;
  return ctx.memory.recall(query, limit);
}
function evalMemoryStore(args, evaluate2, ctx) {
  if (!ctx.memory) return null;
  const content = evaluate2(args[0], ctx);
  const category = args.length > 1 ? evaluate2(args[1], ctx) : void 0;
  const strength = args.length > 2 ? evaluate2(args[2], ctx) : void 0;
  return ctx.memory.store(content, category, strength);
}
function evalMemoryList(args, evaluate2, ctx) {
  if (!ctx.memory) return [];
  const category = args.length > 0 ? evaluate2(args[0], ctx) : void 0;
  return ctx.memory.list(category);
}

// std/trace.ts
function evalTraceEmit(args, evaluate2, ctx) {
  if (!ctx.trace) return;
  const event = evaluate2(args[0], ctx);
  const payload = args.length > 1 ? evaluate2(args[1], ctx) : void 0;
  if (!isEventPayloadValue(payload)) {
    throw new Error(`trace/emit payload for "${event}" is not an event-payload value (JSON-like)`);
  }
  ctx.trace.emit(event, payload);
}
function evalTraceLog(args, evaluate2, ctx) {
  if (!ctx.trace) return;
  const message = evaluate2(args[0], ctx);
  const level = args.length > 1 ? evaluate2(args[1], ctx) : void 0;
  const data = args.length > 2 ? evaluate2(args[2], ctx) : void 0;
  if (!isEventPayloadValue(data)) {
    throw new Error("trace/log data is not an event-payload value (JSON-like)");
  }
  ctx.trace.log(message, level, data);
}

// std/integration.ts
function evalIntegrationHttp(args, evaluate2, ctx) {
  if (!ctx.integration) return null;
  const method = evaluate2(args[0], ctx);
  const url = evaluate2(args[1], ctx);
  const body = args.length > 2 ? evaluate2(args[2], ctx) : void 0;
  if (body !== void 0 && !isFieldValue(body)) {
    throw new Error(`integration/http body for ${method} ${url} is not a field value (JSON-like)`);
  }
  const headers = args.length > 3 ? evaluate2(args[3], ctx) : void 0;
  return ctx.integration.http(method, url, body, headers);
}
function evalIntegrationGithubGetRepo(args, evaluate2, ctx) {
  if (!ctx.integration) return null;
  const owner = evaluate2(args[0], ctx);
  const repo = evaluate2(args[1], ctx);
  return ctx.integration.githubGetRepo(owner, repo);
}
function evalIntegrationGithubCreateIssue(args, evaluate2, ctx) {
  if (!ctx.integration) return null;
  const owner = evaluate2(args[0], ctx);
  const repo = evaluate2(args[1], ctx);
  const title = evaluate2(args[2], ctx);
  const body = args.length > 3 ? evaluate2(args[3], ctx) : void 0;
  return ctx.integration.githubCreateIssue(owner, repo, title, body);
}

// std/vector.ts
function asVec(v) {
  const o = v ?? {};
  const x = typeof o.x === "number" ? o.x : 0;
  const y = typeof o.y === "number" ? o.y : 0;
  const z2 = typeof o.z === "number" ? o.z : void 0;
  return z2 === void 0 ? { x, y } : { x, y, z: z2 };
}
function has3(v) {
  return v.z !== void 0;
}
function z(v) {
  return v.z ?? 0;
}
function make(x, y, zVal, is3d) {
  return is3d ? { x, y, z: zVal } : { x, y };
}
function evalVecAdd(args, evaluate2, ctx) {
  const a = asVec(evaluate2(args[0], ctx));
  const b = asVec(evaluate2(args[1], ctx));
  return make(a.x + b.x, a.y + b.y, z(a) + z(b), has3(a));
}
function evalVecSub(args, evaluate2, ctx) {
  const a = asVec(evaluate2(args[0], ctx));
  const b = asVec(evaluate2(args[1], ctx));
  return make(a.x - b.x, a.y - b.y, z(a) - z(b), has3(a));
}
function evalVecScale(args, evaluate2, ctx) {
  const a = asVec(evaluate2(args[0], ctx));
  const k = evaluate2(args[1], ctx);
  return make(a.x * k, a.y * k, z(a) * k, has3(a));
}
function evalVecDot(args, evaluate2, ctx) {
  const a = asVec(evaluate2(args[0], ctx));
  const b = asVec(evaluate2(args[1], ctx));
  return a.x * b.x + a.y * b.y + z(a) * z(b);
}
function evalVecCross(args, evaluate2, ctx) {
  const a = asVec(evaluate2(args[0], ctx));
  const b = asVec(evaluate2(args[1], ctx));
  if (has3(a)) {
    const ax = a.x, ay = a.y, az = z(a);
    const bx = b.x, by = b.y, bz = z(b);
    return { x: ay * bz - az * by, y: az * bx - ax * bz, z: ax * by - ay * bx };
  }
  return a.x * b.y - a.y * b.x;
}
function evalVecLength(args, evaluate2, ctx) {
  const a = asVec(evaluate2(args[0], ctx));
  return Math.sqrt(a.x * a.x + a.y * a.y + z(a) * z(a));
}
function evalVecLengthSq(args, evaluate2, ctx) {
  const a = asVec(evaluate2(args[0], ctx));
  return a.x * a.x + a.y * a.y + z(a) * z(a);
}
function evalVecNormalize(args, evaluate2, ctx) {
  const a = asVec(evaluate2(args[0], ctx));
  const len = Math.sqrt(a.x * a.x + a.y * a.y + z(a) * z(a));
  if (len === 0) {
    return make(0, 0, 0, has3(a));
  }
  return make(a.x / len, a.y / len, z(a) / len, has3(a));
}
function evalVecDistance(args, evaluate2, ctx) {
  const a = asVec(evaluate2(args[0], ctx));
  const b = asVec(evaluate2(args[1], ctx));
  const dx = a.x - b.x, dy = a.y - b.y, dz = z(a) - z(b);
  return Math.sqrt(dx * dx + dy * dy + dz * dz);
}
function evalVecDistanceSq(args, evaluate2, ctx) {
  const a = asVec(evaluate2(args[0], ctx));
  const b = asVec(evaluate2(args[1], ctx));
  const dx = a.x - b.x, dy = a.y - b.y, dz = z(a) - z(b);
  return dx * dx + dy * dy + dz * dz;
}
function evalVecLerp(args, evaluate2, ctx) {
  const a = asVec(evaluate2(args[0], ctx));
  const b = asVec(evaluate2(args[1], ctx));
  const t = evaluate2(args[2], ctx);
  return make(
    a.x + (b.x - a.x) * t,
    a.y + (b.y - a.y) * t,
    z(a) + (z(b) - z(a)) * t,
    has3(a)
  );
}
function evalVecAngle(args, evaluate2, ctx) {
  const a = asVec(evaluate2(args[0], ctx));
  if (args.length < 2) {
    return Math.atan2(a.y, a.x);
  }
  const b = asVec(evaluate2(args[1], ctx));
  const la = Math.sqrt(a.x * a.x + a.y * a.y + z(a) * z(a));
  const lb = Math.sqrt(b.x * b.x + b.y * b.y + z(b) * z(b));
  if (la === 0 || lb === 0) {
    return 0;
  }
  const d = (a.x * b.x + a.y * b.y + z(a) * z(b)) / (la * lb);
  const clamped = Math.min(Math.max(d, -1), 1);
  return Math.acos(clamped);
}
function evalVecRotate(args, evaluate2, ctx) {
  const a = asVec(evaluate2(args[0], ctx));
  const rad = evaluate2(args[1], ctx);
  const c = Math.cos(rad), s = Math.sin(rad);
  return make(a.x * c - a.y * s, a.x * s + a.y * c, z(a), has3(a));
}
function evalVecClampLength(args, evaluate2, ctx) {
  const a = asVec(evaluate2(args[0], ctx));
  const max = evaluate2(args[1], ctx);
  const len = Math.sqrt(a.x * a.x + a.y * a.y + z(a) * z(a));
  if (len > max) {
    if (len === 0) {
      return make(0, 0, 0, has3(a));
    }
    const k = max / len;
    return make(a.x * k, a.y * k, z(a) * k, has3(a));
  }
  return make(a.x, a.y, z(a), has3(a));
}

// std/geo.ts
function num(o, k) {
  return typeof o[k] === "number" ? o[k] : 0;
}
function asVec2(v) {
  const o = v ?? {};
  const z2 = typeof o.z === "number" ? o.z : void 0;
  return z2 === void 0 ? { x: num(o, "x"), y: num(o, "y") } : { x: num(o, "x"), y: num(o, "y"), z: z2 };
}
function asRect(v) {
  const o = v ?? {};
  return { x: num(o, "x"), y: num(o, "y"), w: num(o, "w"), h: num(o, "h") };
}
function asCircle(v) {
  const o = v ?? {};
  return { x: num(o, "x"), y: num(o, "y"), r: num(o, "r") };
}
function asSegment(v) {
  const o = v ?? {};
  return { x1: num(o, "x1"), y1: num(o, "y1"), x2: num(o, "x2"), y2: num(o, "y2") };
}
function clamp(n, lo, hi) {
  return Math.min(Math.max(n, lo), hi);
}
function evalGeoAabbOverlap(args, evaluate2, ctx) {
  const a = asRect(evaluate2(args[0], ctx));
  const b = asRect(evaluate2(args[1], ctx));
  return a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;
}
function evalGeoCircleOverlap(args, evaluate2, ctx) {
  const a = asCircle(evaluate2(args[0], ctx));
  const b = asCircle(evaluate2(args[1], ctx));
  const dx = a.x - b.x, dy = a.y - b.y;
  return Math.sqrt(dx * dx + dy * dy) <= a.r + b.r;
}
function evalGeoRectCircleOverlap(args, evaluate2, ctx) {
  const r = asRect(evaluate2(args[0], ctx));
  const c = asCircle(evaluate2(args[1], ctx));
  const cx = clamp(c.x, r.x, r.x + r.w);
  const cy = clamp(c.y, r.y, r.y + r.h);
  const dx = c.x - cx, dy = c.y - cy;
  return dx * dx + dy * dy <= c.r * c.r;
}
function evalGeoPointInRect(args, evaluate2, ctx) {
  const p2 = asVec2(evaluate2(args[0], ctx));
  const r = asRect(evaluate2(args[1], ctx));
  return p2.x >= r.x && p2.x <= r.x + r.w && p2.y >= r.y && p2.y <= r.y + r.h;
}
function evalGeoPointInCircle(args, evaluate2, ctx) {
  const p2 = asVec2(evaluate2(args[0], ctx));
  const c = asCircle(evaluate2(args[1], ctx));
  const dx = p2.x - c.x, dy = p2.y - c.y;
  return dx * dx + dy * dy <= c.r * c.r;
}
function evalGeoReflect(args, evaluate2, ctx) {
  const v = asVec2(evaluate2(args[0], ctx));
  const n = asVec2(evaluate2(args[1], ctx));
  const is3d = v.z !== void 0;
  const vz = v.z ?? 0, nz = n.z ?? 0;
  const d = v.x * n.x + v.y * n.y + vz * nz;
  const rx = v.x - n.x * 2 * d;
  const ry = v.y - n.y * 2 * d;
  const rz = vz - nz * 2 * d;
  return is3d ? { x: rx, y: ry, z: rz } : { x: rx, y: ry };
}
function evalGeoSegmentIntersect(args, evaluate2, ctx) {
  const s1 = asSegment(evaluate2(args[0], ctx));
  const s2 = asSegment(evaluate2(args[1], ctx));
  const r_x = s1.x2 - s1.x1, r_y = s1.y2 - s1.y1;
  const s_x = s2.x2 - s2.x1, s_y = s2.y2 - s2.y1;
  const denom = r_x * s_y - r_y * s_x;
  if (denom === 0) {
    return null;
  }
  const qpx = s2.x1 - s1.x1, qpy = s2.y1 - s1.y1;
  const t = (qpx * s_y - qpy * s_x) / denom;
  const u = (qpx * r_y - qpy * r_x) / denom;
  if (t < 0 || t > 1 || u < 0 || u > 1) {
    return null;
  }
  return { x: s1.x1 + t * r_x, y: s1.y1 + t * r_y };
}

// std/grid.ts
function num2(o, k) {
  return typeof o[k] === "number" ? o[k] : 0;
}
function asCell(v) {
  const o = v ?? {};
  return { x: num2(o, "x"), y: num2(o, "y") };
}
function evalGridToWorld(args, evaluate2, ctx) {
  const cell = asCell(evaluate2(args[0], ctx));
  const cellSize = evaluate2(args[1], ctx);
  return { x: (cell.x + 0.5) * cellSize, y: (cell.y + 0.5) * cellSize };
}
function evalGridFromWorld(args, evaluate2, ctx) {
  const point = asCell(evaluate2(args[0], ctx));
  const cellSize = evaluate2(args[1], ctx);
  return { x: Math.floor(point.x / cellSize), y: Math.floor(point.y / cellSize) };
}
function evalGridIsoToScreen(args, evaluate2, ctx) {
  const cell = asCell(evaluate2(args[0], ctx));
  const tileW = evaluate2(args[1], ctx);
  const tileH = evaluate2(args[2], ctx);
  return { x: (cell.x - cell.y) * tileW / 2, y: (cell.x + cell.y) * tileH / 2 };
}
function evalGridScreenToIso(args, evaluate2, ctx) {
  const point = asCell(evaluate2(args[0], ctx));
  const tileW = evaluate2(args[1], ctx);
  const tileH = evaluate2(args[2], ctx);
  const halfW = tileW / 2;
  const halfH = tileH / 2;
  return {
    x: (point.x / halfW + point.y / halfH) / 2,
    y: (point.y / halfH - point.x / halfW) / 2
  };
}
function evalGridDistance(args, evaluate2, ctx) {
  const a = asCell(evaluate2(args[0], ctx));
  const b = asCell(evaluate2(args[1], ctx));
  return Math.max(Math.abs(a.x - b.x), Math.abs(a.y - b.y));
}
function evalGridManhattanDistance(args, evaluate2, ctx) {
  const a = asCell(evaluate2(args[0], ctx));
  const b = asCell(evaluate2(args[1], ctx));
  return Math.abs(a.x - b.x) + Math.abs(a.y - b.y);
}
function evalGridNeighbors(args, evaluate2, ctx) {
  const cell = asCell(evaluate2(args[0], ctx));
  const diagonal = args.length > 1 ? Boolean(evaluate2(args[1], ctx)) : false;
  const result = [
    { x: cell.x, y: cell.y - 1 },
    // N
    { x: cell.x + 1, y: cell.y },
    // E
    { x: cell.x, y: cell.y + 1 },
    // S
    { x: cell.x - 1, y: cell.y }
    // W
  ];
  if (diagonal) {
    result.push(
      { x: cell.x + 1, y: cell.y - 1 },
      // NE
      { x: cell.x + 1, y: cell.y + 1 },
      // SE
      { x: cell.x - 1, y: cell.y + 1 },
      // SW
      { x: cell.x - 1, y: cell.y - 1 }
      // NW
    );
  }
  return result;
}
function evalGridCellsInRadius(args, evaluate2, ctx) {
  const cell = asCell(evaluate2(args[0], ctx));
  const r = evaluate2(args[1], ctx);
  const rf = Math.floor(r);
  const r2 = r * r;
  const result = [];
  for (let dy = -rf; dy <= rf; dy++) {
    for (let dx = -rf; dx <= rf; dx++) {
      if (dx * dx + dy * dy <= r2) {
        result.push({ x: cell.x + dx, y: cell.y + dy });
      }
    }
  }
  return result;
}
function evalGridLine(args, evaluate2, ctx) {
  const a = asCell(evaluate2(args[0], ctx));
  const b = asCell(evaluate2(args[1], ctx));
  let x0 = a.x, y0 = a.y;
  const x1 = b.x, y1 = b.y;
  const dx = Math.abs(x1 - x0);
  const dy = Math.abs(y1 - y0);
  const sx = x0 < x1 ? 1 : -1;
  const sy = y0 < y1 ? 1 : -1;
  let err = dx - dy;
  const result = [];
  for (; ; ) {
    result.push({ x: x0, y: y0 });
    if (x0 === x1 && y0 === y1) {
      break;
    }
    const e2 = 2 * err;
    if (e2 > -dy) {
      err -= dy;
      x0 += sx;
    }
    if (e2 < dx) {
      err += dx;
      y0 += sy;
    }
  }
  return result;
}
function evalGridInBounds(args, evaluate2, ctx) {
  const cell = asCell(evaluate2(args[0], ctx));
  const w = evaluate2(args[1], ctx);
  const h = evaluate2(args[2], ctx);
  return cell.x >= 0 && cell.x < w && cell.y >= 0 && cell.y < h;
}

// std/anim.ts
function evalAnimFrameAt(args, evaluate2, ctx) {
  const elapsed = evaluate2(args[0], ctx);
  const frameDur = evaluate2(args[1], ctx);
  const frameCount = evaluate2(args[2], ctx);
  const loop = args.length > 3 ? Boolean(evaluate2(args[3], ctx)) : true;
  if (frameCount <= 0) {
    return 0;
  }
  const i = Math.floor(elapsed / frameDur);
  if (loop) {
    return (i % frameCount + frameCount) % frameCount;
  }
  return Math.min(i, frameCount - 1);
}
function evalAnimSheetRect(args, evaluate2, ctx) {
  const frameIndex = evaluate2(args[0], ctx);
  const cols = evaluate2(args[1], ctx);
  const frameW = evaluate2(args[2], ctx);
  const frameH = evaluate2(args[3], ctx);
  return {
    sx: frameIndex % cols * frameW,
    sy: Math.floor(frameIndex / cols) * frameH,
    sw: frameW,
    sh: frameH
  };
}
function evalAnimDirectionFromDelta(args, evaluate2, ctx) {
  const dx = evaluate2(args[0], ctx);
  const dy = evaluate2(args[1], ctx);
  const ways = args.length > 2 ? evaluate2(args[2], ctx) : 4;
  const angle = Math.atan2(dy, dx);
  if (ways === 8) {
    const sector2 = Math.round(angle / (Math.PI / 4));
    const idx2 = (sector2 % 8 + 8) % 8;
    const dirs8 = [
      "right",
      "down-right",
      "down",
      "down-left",
      "left",
      "up-left",
      "up",
      "up-right"
    ];
    return dirs8[idx2];
  }
  const sector = Math.round(angle / (Math.PI / 2));
  const idx = (sector % 4 + 4) % 4;
  const dirs4 = ["right", "down", "left", "up"];
  return dirs4[idx];
}

// std/ease.ts
function clamp01(t) {
  return Math.min(Math.max(t, 0), 1);
}
function applyCurve(curve, tRaw) {
  const t = clamp01(tRaw);
  switch (curve) {
    case "in-quad":
      return t * t;
    case "out-quad":
      return t * (2 - t);
    case "in-out-quad":
      return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
    case "in-cubic":
      return t * t * t;
    case "out-cubic": {
      const u = t - 1;
      return u * u * u + 1;
    }
    case "in-out-cubic":
      return t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1;
    case "elastic-out": {
      if (t === 0) {
        return 0;
      }
      if (t === 1) {
        return 1;
      }
      const c4 = 2 * Math.PI / 3;
      return Math.pow(2, -10 * t) * Math.sin((t * 10 - 0.75) * c4) + 1;
    }
    case "bounce-out": {
      const n1 = 7.5625;
      const d1 = 2.75;
      let u = t;
      if (u < 1 / d1) {
        return n1 * u * u;
      } else if (u < 2 / d1) {
        u -= 1.5 / d1;
        return n1 * u * u + 0.75;
      } else if (u < 2.5 / d1) {
        u -= 2.25 / d1;
        return n1 * u * u + 0.9375;
      } else {
        u -= 2.625 / d1;
        return n1 * u * u + 0.984375;
      }
    }
    case "linear":
    default:
      return t;
  }
}
function evalEaseApply(args, evaluate2, ctx) {
  const curve = evaluate2(args[0], ctx);
  const t = evaluate2(args[1], ctx);
  return applyCurve(curve, t);
}
function evalEaseSmoothstep(args, evaluate2, ctx) {
  const edge0 = evaluate2(args[0], ctx);
  const edge1 = evaluate2(args[1], ctx);
  const x = evaluate2(args[2], ctx);
  if (edge0 === edge1) {
    return 0;
  }
  const t = clamp01((x - edge0) / (edge1 - edge0));
  return t * t * (3 - 2 * t);
}

// std/noise.ts
var PERM = [
  151,
  160,
  137,
  91,
  90,
  15,
  131,
  13,
  201,
  95,
  96,
  53,
  194,
  233,
  7,
  225,
  140,
  36,
  103,
  30,
  69,
  142,
  8,
  99,
  37,
  240,
  21,
  10,
  23,
  190,
  6,
  148,
  247,
  120,
  234,
  75,
  0,
  26,
  197,
  62,
  94,
  252,
  219,
  203,
  117,
  35,
  11,
  32,
  57,
  177,
  33,
  88,
  237,
  149,
  56,
  87,
  174,
  20,
  125,
  136,
  171,
  168,
  68,
  175,
  74,
  165,
  71,
  134,
  139,
  48,
  27,
  166,
  77,
  146,
  158,
  231,
  83,
  111,
  229,
  122,
  60,
  211,
  133,
  230,
  220,
  105,
  92,
  41,
  55,
  46,
  245,
  40,
  244,
  102,
  143,
  54,
  65,
  25,
  63,
  161,
  1,
  216,
  80,
  73,
  209,
  76,
  132,
  187,
  208,
  89,
  18,
  169,
  200,
  196,
  135,
  130,
  116,
  188,
  159,
  86,
  164,
  100,
  109,
  198,
  173,
  186,
  3,
  64,
  52,
  217,
  226,
  250,
  124,
  123,
  5,
  202,
  38,
  147,
  118,
  126,
  255,
  82,
  85,
  212,
  207,
  206,
  59,
  227,
  47,
  16,
  58,
  17,
  182,
  189,
  28,
  42,
  223,
  183,
  170,
  213,
  119,
  248,
  152,
  2,
  44,
  154,
  163,
  70,
  221,
  153,
  101,
  155,
  167,
  43,
  172,
  9,
  129,
  22,
  39,
  253,
  19,
  98,
  108,
  110,
  79,
  113,
  224,
  232,
  178,
  185,
  112,
  104,
  218,
  246,
  97,
  228,
  251,
  34,
  242,
  193,
  238,
  210,
  144,
  12,
  191,
  179,
  162,
  241,
  81,
  51,
  145,
  235,
  249,
  14,
  239,
  107,
  49,
  192,
  214,
  31,
  181,
  199,
  106,
  157,
  184,
  84,
  204,
  176,
  115,
  121,
  50,
  45,
  127,
  4,
  150,
  254,
  138,
  236,
  205,
  93,
  222,
  114,
  67,
  29,
  24,
  72,
  243,
  141,
  128,
  195,
  78,
  66,
  215,
  61,
  156,
  180
];
function p(i) {
  return PERM[i & 255];
}
function fade(t) {
  return t * t * t * (t * (t * 6 - 15) + 10);
}
function lerp(a, b, t) {
  return a + t * (b - a);
}
function grad2(hash, x, y) {
  const h = hash & 7;
  const u = h < 4 ? x : y;
  const v = h < 4 ? y : x;
  return ((h & 1) === 0 ? u : -u) + ((h & 2) === 0 ? v : -v);
}
function perlin(x, y, seed) {
  const s = Math.floor(seed) & 255;
  const X = Math.floor(x) + s & 255;
  const Y = Math.floor(y) + s & 255;
  const xf = x - Math.floor(x);
  const yf = y - Math.floor(y);
  const u = fade(xf);
  const v = fade(yf);
  const aa = p(p(X) + Y);
  const ab = p(p(X) + Y + 1);
  const ba = p(p(X + 1) + Y);
  const bb = p(p(X + 1) + Y + 1);
  const x1 = lerp(grad2(aa, xf, yf), grad2(ba, xf - 1, yf), u);
  const x2 = lerp(grad2(ab, xf, yf - 1), grad2(bb, xf - 1, yf - 1), u);
  return lerp(x1, x2, v);
}
function simplex(x, y, seed) {
  return (perlin(x, y, seed) + perlin(x * 2 + 5.2, y * 2 + 1.3, seed) * 0.5 + perlin(x * 4 + 9.1, y * 4 + 7.7, seed) * 0.25) / 1.75;
}
function fbm(x, y, octaves, seed) {
  let n = Math.floor(octaves);
  if (n < 1) n = 1;
  if (n > 8) n = 8;
  let total = 0;
  let freq = 1;
  let amp = 1;
  let max = 0;
  for (let i = 0; i < n; i++) {
    total += perlin(x * freq, y * freq, seed) * amp;
    max += amp;
    freq *= 2;
    amp *= 0.5;
  }
  return total / max;
}
function evalNoisePerlin(args, evaluate2, ctx) {
  const x = evaluate2(args[0], ctx);
  const y = args.length > 1 ? evaluate2(args[1], ctx) : 0;
  const seed = args.length > 2 ? evaluate2(args[2], ctx) : 0;
  return perlin(x, y, seed);
}
function evalNoiseSimplex(args, evaluate2, ctx) {
  const x = evaluate2(args[0], ctx);
  const y = evaluate2(args[1], ctx);
  const seed = args.length > 2 ? evaluate2(args[2], ctx) : 0;
  return simplex(x, y, seed);
}
function evalNoiseFbm(args, evaluate2, ctx) {
  const x = evaluate2(args[0], ctx);
  const y = evaluate2(args[1], ctx);
  const octaves = evaluate2(args[2], ctx);
  const seed = args.length > 3 ? evaluate2(args[3], ctx) : 0;
  return fbm(x, y, octaves, seed);
}

// std/path.ts
function asCell2(v) {
  const o = v ?? {};
  const x = typeof o.x === "number" ? o.x : 0;
  const y = typeof o.y === "number" ? o.y : 0;
  return { x, y };
}
function key(x, y) {
  return `${x},${y}`;
}
var DIRS4 = [
  [0, -1],
  [1, 0],
  [0, 1],
  [-1, 0]
];
var DIRS_DIAG = [
  [1, -1],
  [1, 1],
  [-1, 1],
  [-1, -1]
];
function neighbors(diagonal) {
  return diagonal ? [...DIRS4, ...DIRS_DIAG] : DIRS4;
}
function blockedSet(blocked) {
  const set = /* @__PURE__ */ new Set();
  for (const b of blocked) {
    const c = asCell2(b);
    set.add(key(c.x, c.y));
  }
  return set;
}
function evalPathAstar(args, evaluate2, ctx) {
  const start = asCell2(evaluate2(args[0], ctx));
  const goal = asCell2(evaluate2(args[1], ctx));
  const blockedArr = evaluate2(args[2], ctx) ?? [];
  const w = evaluate2(args[3], ctx);
  const h = evaluate2(args[4], ctx);
  const diagonal = args.length > 5 ? Boolean(evaluate2(args[5], ctx)) : false;
  const blocked = blockedSet(blockedArr);
  const dirs = neighbors(diagonal);
  const heuristic = (x, y) => {
    const dx = Math.abs(x - goal.x);
    const dy = Math.abs(y - goal.y);
    return diagonal ? Math.max(dx, dy) : dx + dy;
  };
  const startKey = key(start.x, start.y);
  const goalKey = key(goal.x, goal.y);
  if (start.x < 0 || start.x >= w || start.y < 0 || start.y >= h || goal.x < 0 || goal.x >= w || goal.y < 0 || goal.y >= h || blocked.has(startKey) || blocked.has(goalKey)) {
    return [];
  }
  const open = [];
  const gScore = /* @__PURE__ */ new Map();
  const cameFrom = /* @__PURE__ */ new Map();
  let counter = 0;
  gScore.set(startKey, 0);
  open.push({
    x: start.x,
    y: start.y,
    g: 0,
    h: heuristic(start.x, start.y),
    f: heuristic(start.x, start.y),
    order: counter++
  });
  while (open.length > 0) {
    let bestIdx = 0;
    for (let i = 1; i < open.length; i++) {
      const a = open[i];
      const b = open[bestIdx];
      if (a.f < b.f || a.f === b.f && a.h < b.h || a.f === b.f && a.h === b.h && a.order < b.order) {
        bestIdx = i;
      }
    }
    const current = open[bestIdx];
    open.splice(bestIdx, 1);
    const curKey = key(current.x, current.y);
    if (curKey === goalKey) {
      const path = [];
      let k = curKey;
      while (k !== void 0) {
        const parts = k.split(",");
        path.push({ x: Number(parts[0]), y: Number(parts[1]) });
        k = cameFrom.get(k);
      }
      path.reverse();
      return path;
    }
    const recordedG = gScore.get(curKey);
    if (recordedG !== void 0 && current.g > recordedG) {
      continue;
    }
    for (const [dx, dy] of dirs) {
      const nx = current.x + dx;
      const ny = current.y + dy;
      if (nx < 0 || nx >= w || ny < 0 || ny >= h) {
        continue;
      }
      const nKey = key(nx, ny);
      if (blocked.has(nKey)) {
        continue;
      }
      const tentativeG = current.g + 1;
      const existingG = gScore.get(nKey);
      if (existingG === void 0 || tentativeG < existingG) {
        gScore.set(nKey, tentativeG);
        cameFrom.set(nKey, curKey);
        const nh = heuristic(nx, ny);
        open.push({
          x: nx,
          y: ny,
          g: tentativeG,
          h: nh,
          f: tentativeG + nh,
          order: counter++
        });
      }
    }
  }
  return [];
}
function evalPathReachable(args, evaluate2, ctx) {
  const start = asCell2(evaluate2(args[0], ctx));
  const steps = Math.floor(evaluate2(args[1], ctx));
  const blockedArr = evaluate2(args[2], ctx) ?? [];
  const w = evaluate2(args[3], ctx);
  const h = evaluate2(args[4], ctx);
  const diagonal = args.length > 5 ? Boolean(evaluate2(args[5], ctx)) : false;
  const blocked = blockedSet(blockedArr);
  const dirs = neighbors(diagonal);
  const startKey = key(start.x, start.y);
  const visited = /* @__PURE__ */ new Set();
  if (start.x < 0 || start.x >= w || start.y < 0 || start.y >= h || blocked.has(startKey)) {
    return [];
  }
  visited.add(startKey);
  let frontier = [{ x: start.x, y: start.y }];
  for (let depth = 0; depth < steps; depth++) {
    const next = [];
    for (const cell of frontier) {
      for (const [dx, dy] of dirs) {
        const nx = cell.x + dx;
        const ny = cell.y + dy;
        if (nx < 0 || nx >= w || ny < 0 || ny >= h) {
          continue;
        }
        const nKey = key(nx, ny);
        if (blocked.has(nKey) || visited.has(nKey)) {
          continue;
        }
        visited.add(nKey);
        next.push({ x: nx, y: ny });
      }
    }
    frontier = next;
  }
  const result = [];
  for (const k of visited) {
    const parts = k.split(",");
    result.push({ x: Number(parts[0]), y: Number(parts[1]) });
  }
  result.sort((a, b) => a.y !== b.y ? a.y - b.y : a.x - b.x);
  return result;
}

// SExpressionEvaluator.ts
var UNKNOWN_OPERATOR = /* @__PURE__ */ Symbol("unknown-operator");
function assertNotAmbiguousConcat(op) {
  if (op === "concat") {
    throw new Error(
      "Ambiguous operator 'concat': use 'str/concat' (string) or 'array/concat' (array)"
    );
  }
}
function evalQuote(args) {
  const body = args[0];
  if (typeof body !== "string") {
    throw new Error("quote: expected an encoded quote body (string)");
  }
  return decodeQuoteBody(body);
}
var OPERATOR_TABLE = {
  "+": evalAdd,
  "-": evalSubtract,
  "*": evalMultiply,
  "/": evalDivide,
  "%": evalModulo,
  "abs": evalAbs,
  "min": evalMin,
  "max": evalMax,
  "floor": evalFloor,
  "ceil": evalCeil,
  "round": evalRound,
  "clamp": evalClamp,
  "=": evalEqual,
  "==": evalEqual,
  "!=": evalNotEqual,
  "<": evalLessThan,
  ">": evalGreaterThan,
  "<=": evalLessThanOrEqual,
  ">=": evalGreaterThanOrEqual,
  "matches": evalMatches,
  "and": evalAnd,
  "or": evalOr,
  "not": evalNot,
  "if": evalIf,
  "let": evalLet,
  "do": evalDo,
  "when": evalWhen,
  "fn": evalFn,
  "lambda": evalFn,
  // G-CROSS-041: an S-expression held as data — its encoded body decodes verbatim, never evaluated.
  "quote": evalQuote,
  "quasiquote": evalQuasiquote,
  "behavior/apply": evalBehaviorApply,
  "behavior/ref": evalBehaviorRef,
  "unquote": evalUnquote,
  "map": evalMap,
  "filter": evalFilter,
  "find": evalFind,
  "count": evalCount,
  "sum": evalSum,
  "first": evalFirst,
  "last": evalLast,
  "nth": evalNth,
  "includes": evalIncludes,
  // Legacy bare alias for 'str/join' — orbital-core's evaluator already
  // dispatches both spellings to the same op (`"join" | "str/join" =>
  // K::JoinOp`, evaluator/operators/mod.rs:121); this interpreter had no
  // entry at all for the bare form, so `(join arr sep)` fell through to the
  // generic "unknown operator, treat as literal data" path instead of
  // erroring OR joining — confirmed live via project-friday's Global Search
  // (L-26, `Almadar_LOLO_Gaps.md`).
  "join": evalStrJoin,
  "empty": evalEmpty,
  "list": evalList,
  "set": (args, evaluate2, ctx) => {
    evalSet(args, evaluate2, ctx);
    return void 0;
  },
  "set-dynamic": (args, evaluate2, ctx) => {
    evalSetDynamic(args, evaluate2, ctx);
    return void 0;
  },
  "increment": (args, evaluate2, ctx) => {
    evalIncrement(args, evaluate2, ctx);
    return void 0;
  },
  "decrement": (args, evaluate2, ctx) => {
    evalDecrement(args, evaluate2, ctx);
    return void 0;
  },
  "emit": (args, evaluate2, ctx) => {
    evalEmit(args, evaluate2, ctx);
    return void 0;
  },
  "persist": (args, evaluate2, ctx) => {
    evalPersist(args, evaluate2, ctx);
    return void 0;
  },
  "navigate": (args, evaluate2, ctx) => {
    evalNavigate(args, evaluate2, ctx);
    return void 0;
  },
  "notify": (args, evaluate2, ctx) => {
    evalNotify(args, evaluate2, ctx);
    return void 0;
  },
  "spawn": (args, evaluate2, ctx) => {
    evalSpawn(args, evaluate2, ctx);
    return void 0;
  },
  "despawn": (args, evaluate2, ctx) => {
    evalDespawn(args, evaluate2, ctx);
    return void 0;
  },
  "call-service": (args, evaluate2, ctx) => {
    evalCallService(args, evaluate2, ctx);
    return void 0;
  },
  "render-ui": (args, evaluate2, ctx) => {
    evalRenderUI(args, evaluate2, ctx);
    return void 0;
  },
  "ref": evalRef,
  "deref": evalDeref,
  "swap": evalSwap,
  "watch": (args, evaluate2, ctx) => {
    evalWatch(args, evaluate2, ctx);
    return void 0;
  },
  "atomic": evalAtomic,
  "math/abs": evalMathAbs,
  "math/min": evalMathMin,
  "math/max": evalMathMax,
  "math/clamp": evalMathClamp,
  "math/floor": evalMathFloor,
  "math/ceil": evalMathCeil,
  "math/round": evalMathRound,
  "math/pow": evalMathPow,
  "math/sqrt": evalMathSqrt,
  "math/log10": evalMathLog10,
  "math/ln": evalMathLn,
  "math/mod": evalMathMod,
  "math/sign": evalMathSign,
  "math/lerp": evalMathLerp,
  "math/map": evalMathMap,
  "math/random": () => evalMathRandom(),
  "math/randomInt": evalMathRandomInt,
  "math/default": evalMathDefault,
  "math/sin": evalMathSin,
  "math/cos": evalMathCos,
  "math/tan": evalMathTan,
  "math/atan2": evalMathAtan2,
  "math/asin": evalMathAsin,
  "math/acos": evalMathAcos,
  "math/atan": evalMathAtan,
  "math/hypot": evalMathHypot,
  "math/deg-rad": evalMathDegRad,
  "math/rad-deg": evalMathRadDeg,
  "math/wrap": evalMathWrap,
  "math/approach": evalMathApproach,
  "vec/add": evalVecAdd,
  "vec/sub": evalVecSub,
  "vec/scale": evalVecScale,
  "vec/dot": evalVecDot,
  "vec/cross": evalVecCross,
  "vec/length": evalVecLength,
  "vec/length-sq": evalVecLengthSq,
  "vec/normalize": evalVecNormalize,
  "vec/distance": evalVecDistance,
  "vec/distance-sq": evalVecDistanceSq,
  "vec/lerp": evalVecLerp,
  "vec/angle": evalVecAngle,
  "vec/rotate": evalVecRotate,
  "vec/clamp-length": evalVecClampLength,
  "geo/aabb-overlap": evalGeoAabbOverlap,
  "geo/circle-overlap": evalGeoCircleOverlap,
  "geo/rect-circle-overlap": evalGeoRectCircleOverlap,
  "geo/point-in-rect": evalGeoPointInRect,
  "geo/point-in-circle": evalGeoPointInCircle,
  "geo/reflect": evalGeoReflect,
  "geo/segment-intersect": evalGeoSegmentIntersect,
  "grid/to-world": evalGridToWorld,
  "grid/from-world": evalGridFromWorld,
  "grid/iso-to-screen": evalGridIsoToScreen,
  "grid/screen-to-iso": evalGridScreenToIso,
  "grid/distance": evalGridDistance,
  "grid/manhattan-distance": evalGridManhattanDistance,
  "grid/neighbors": evalGridNeighbors,
  "grid/cells-in-radius": evalGridCellsInRadius,
  "grid/line": evalGridLine,
  "grid/in-bounds": evalGridInBounds,
  "anim/frame-at": evalAnimFrameAt,
  "anim/sheet-rect": evalAnimSheetRect,
  "anim/direction-from-delta": evalAnimDirectionFromDelta,
  "ease/apply": evalEaseApply,
  "ease/smoothstep": evalEaseSmoothstep,
  "noise/perlin": evalNoisePerlin,
  "noise/simplex": evalNoiseSimplex,
  "noise/fbm": evalNoiseFbm,
  "path/astar": evalPathAstar,
  "path/reachable": evalPathReachable,
  "str/len": evalStrLen,
  "str/concat": evalStrConcat,
  "str/upper": evalStrUpper,
  "str/lower": evalStrLower,
  "str/trim": evalStrTrim,
  "str/trimStart": evalStrTrimStart,
  "str/trimEnd": evalStrTrimEnd,
  "str/split": evalStrSplit,
  "str/join": evalStrJoin,
  "str/slice": evalStrSlice,
  "str/replace": evalStrReplace,
  "str/replaceAll": evalStrReplaceAll,
  "str/includes": evalStrIncludes,
  "str/startsWith": evalStrStartsWith,
  "str/endsWith": evalStrEndsWith,
  "str/padStart": evalStrPadStart,
  "str/padEnd": evalStrPadEnd,
  "str/repeat": evalStrRepeat,
  "str/reverse": evalStrReverse,
  "str/capitalize": evalStrCapitalize,
  "str/titleCase": evalStrTitleCase,
  "str/camelCase": evalStrCamelCase,
  "str/kebabCase": evalStrKebabCase,
  "str/snakeCase": evalStrSnakeCase,
  "str/default": evalStrDefault,
  "str/template": evalStrTemplate,
  "str/truncate": evalStrTruncate,
  "to-string": (args, evaluate2, ctx) => String(evaluate2(args[0], ctx)),
  "array/len": evalArrayLen,
  "array/range": evalArrayRange,
  "array/first": evalArrayFirst,
  "array/last": evalArrayLast,
  "array/nth": evalArrayNth,
  "array/slice": evalArraySlice,
  "array/concat": evalArrayConcat,
  "array/append": evalArrayAppend,
  "array/prepend": evalArrayPrepend,
  "array/insert": evalArrayInsert,
  "array/remove": evalArrayRemove,
  "array/removeItem": evalArrayRemoveItem,
  "array/reverse": evalArrayReverse,
  "array/sort": evalArraySort,
  "array/shuffle": evalArrayShuffle,
  "array/unique": evalArrayUnique,
  "array/flatten": evalArrayFlatten,
  "array/zip": evalArrayZip,
  "array/includes": evalArrayIncludes,
  "array/indexOf": evalArrayIndexOf,
  "array/find": evalArrayFind,
  "array/findIndex": evalArrayFindIndex,
  "array/filter": evalArrayFilter,
  "array/reject": evalArrayReject,
  "array/map": evalArrayMap,
  "array/reduce": evalArrayReduce,
  "array/every": evalArrayEvery,
  "array/some": evalArraySome,
  "array/count": evalArrayCount,
  "array/sum": evalArraySum,
  "array/avg": evalArrayAvg,
  "array/min": evalArrayMin,
  "array/max": evalArrayMax,
  "array/groupBy": evalArrayGroupBy,
  "array/partition": evalArrayPartition,
  "array/take": evalArrayTake,
  "array/drop": evalArrayDrop,
  "array/takeLast": evalArrayTakeLast,
  "array/dropLast": evalArrayDropLast,
  "array/cosine": evalArrayCosine,
  "array/nearest": evalArrayNearest,
  "object/keys": evalObjectKeys,
  "object/values": evalObjectValues,
  "object/entries": evalObjectEntries,
  "object/fromEntries": evalObjectFromEntries,
  "object/get": evalObjectGet,
  "object/set": evalObjectSet,
  "object/has": evalObjectHas,
  "object/merge": evalObjectMerge,
  "object/deepMerge": evalObjectDeepMerge,
  "object/pick": evalObjectPick,
  "object/omit": evalObjectOmit,
  "object/mapValues": evalObjectMapValues,
  "object/mapKeys": evalObjectMapKeys,
  "object/filter": evalObjectFilter,
  "object/equals": evalObjectEquals,
  "object/clone": evalObjectClone,
  "object/deepClone": evalObjectDeepClone,
  "path": evalPath,
  "json/parse": evalJsonParse,
  "json/stringify": evalJsonStringify,
  "validate/required": evalValidateRequired,
  "validate/string": evalValidateString,
  "validate/number": evalValidateNumber,
  "validate/boolean": evalValidateBoolean,
  "validate/array": evalValidateArray,
  "validate/object": evalValidateObject,
  "validate/email": evalValidateEmail,
  "validate/url": evalValidateUrl,
  "validate/uuid": evalValidateUuid,
  "validate/phone": evalValidatePhone,
  "validate/creditCard": evalValidateCreditCard,
  "validate/date": evalValidateDate,
  "validate/minLength": evalValidateMinLength,
  "validate/maxLength": evalValidateMaxLength,
  "validate/length": evalValidateLength,
  "validate/min": evalValidateMin,
  "validate/max": evalValidateMax,
  "validate/range": evalValidateRange,
  "validate/pattern": evalValidatePattern,
  "validate/oneOf": evalValidateOneOf,
  "validate/noneOf": evalValidateNoneOf,
  "validate/equals": evalValidateEquals,
  "validate/check": evalValidateCheck,
  "time/now": (_args, _evaluate, ctx) => evalTimeNow(ctx),
  "time/today": (_args, _evaluate, ctx) => evalTimeToday(ctx),
  "time/parse": evalTimeParse,
  "time/format": evalTimeFormat,
  "time/year": evalTimeYear,
  "time/month": evalTimeMonth,
  "time/day": evalTimeDay,
  "time/weekday": evalTimeWeekday,
  "time/hour": evalTimeHour,
  "time/minute": evalTimeMinute,
  "time/second": evalTimeSecond,
  "time/add": evalTimeAdd,
  "time/subtract": evalTimeSubtract,
  "time/diff": evalTimeDiff,
  "time/startOf": evalTimeStartOf,
  "time/endOf": evalTimeEndOf,
  "time/isBefore": evalTimeIsBefore,
  "time/isAfter": evalTimeIsAfter,
  "time/isBetween": evalTimeIsBetween,
  "time/isSame": evalTimeIsSame,
  "time/isPast": evalTimeIsPast,
  "time/isFuture": evalTimeIsFuture,
  "time/isToday": evalTimeIsToday,
  "time/relative": evalTimeRelative,
  "time/duration": evalTimeDuration,
  "format/number": evalFormatNumber,
  "format/currency": evalFormatCurrency,
  "i18n/t": evalI18nT,
  "format/percent": evalFormatPercent,
  "format/bytes": evalFormatBytes,
  "format/ordinal": evalFormatOrdinal,
  "format/plural": evalFormatPlural,
  "format/list": evalFormatList,
  "format/phone": evalFormatPhone,
  "format/creditCard": evalFormatCreditCard,
  "async/delay": evalAsyncDelay,
  "async/interval": evalAsyncInterval,
  "async/timeout": evalAsyncTimeout,
  "async/debounce": (args, evaluate2, ctx) => {
    evalAsyncDebounce(args, evaluate2, ctx);
    return void 0;
  },
  "async/throttle": (args, evaluate2, ctx) => {
    evalAsyncThrottle(args, evaluate2, ctx);
    return void 0;
  },
  "async/retry": evalAsyncRetry,
  "async/race": evalAsyncRace,
  "async/all": evalAsyncAll,
  "async/sequence": evalAsyncSequence,
  "prob/seed": (args, evaluate2, ctx) => {
    evalProbSeed(args, evaluate2, ctx);
    return void 0;
  },
  "prob/flip": evalProbFlip,
  "prob/gaussian": evalProbGaussian,
  "prob/uniform": evalProbUniform,
  "prob/beta": evalProbBeta,
  "prob/categorical": evalProbCategorical,
  "prob/poisson": evalProbPoisson,
  "prob/condition": (args, evaluate2, ctx) => {
    evalProbCondition(args, evaluate2, ctx);
    return void 0;
  },
  "prob/sample": evalProbSample,
  "prob/posterior": evalProbPosterior,
  "prob/infer": evalProbInfer,
  "prob/expected-value": evalProbExpectedValue,
  "prob/variance": evalProbVariance,
  "prob/histogram": evalProbHistogram,
  "prob/percentile": evalProbPercentile,
  "prob/credible-interval": evalProbCredibleInterval,
  "os/watch-files": (args, evaluate2, ctx) => {
    evalOsWatchFiles(args, evaluate2, ctx);
    return void 0;
  },
  "os/watch-process": (args, evaluate2, ctx) => {
    evalOsWatchProcess(args, evaluate2, ctx);
    return void 0;
  },
  "os/watch-port": (args, evaluate2, ctx) => {
    evalOsWatchPort(args, evaluate2, ctx);
    return void 0;
  },
  "os/watch-http": (args, evaluate2, ctx) => {
    evalOsWatchHttp(args, evaluate2, ctx);
    return void 0;
  },
  "os/watch-cron": (args, evaluate2, ctx) => {
    evalOsWatchCron(args, evaluate2, ctx);
    return void 0;
  },
  "os/watch-signal": (args, evaluate2, ctx) => {
    evalOsWatchSignal(args, evaluate2, ctx);
    return void 0;
  },
  "os/watch-env": (args, evaluate2, ctx) => {
    evalOsWatchEnv(args, evaluate2, ctx);
    return void 0;
  },
  "os/debounce": (args, evaluate2, ctx) => {
    evalOsDebounce(args, evaluate2, ctx);
    return void 0;
  },
  "llm/generate": evalLlmGenerate,
  "llm/call-tools": evalLlmCallTools,
  "llm/embed": evalLlmEmbed,
  "llm/token-count": evalLlmTokenCount,
  "llm/switch": evalLlmSwitch,
  "llm/compact": evalLlmCompact,
  "workspace/read-orbital": evalWorkspaceReadOrbital,
  "workspace/write-orbital": evalWorkspaceWriteOrbital,
  "workspace/read-file": evalWorkspaceReadFile,
  "workspace/write-file": evalWorkspaceWriteFile,
  "workspace/exists": evalWorkspaceExists,
  "workspace/list-orbitals": evalWorkspaceListOrbitals,
  "workspace/read-schema": evalWorkspaceReadSchema,
  "workspace/write-schema": evalWorkspaceWriteSchema,
  "workspace/read-plan": evalWorkspaceReadPlan,
  "workspace/write-plan": evalWorkspaceWritePlan,
  "workspace/archive-orbital": evalWorkspaceArchiveOrbital,
  "session/read-spec": evalSessionReadSpec,
  "session/write-spec": evalSessionWriteSpec,
  "session/read-memory": evalSessionReadMemory,
  "session/write-memory": evalSessionWriteMemory,
  "session/read-history": evalSessionReadHistory,
  "session/append-history": evalSessionAppendHistory,
  "session/read-errors": evalSessionReadErrors,
  "session/write-errors": evalSessionWriteErrors,
  "session/read-analysis": evalSessionReadAnalysis,
  "memory/recall": evalMemoryRecall,
  "memory/store": evalMemoryStore,
  "memory/list": evalMemoryList,
  "trace/emit": evalTraceEmit,
  "trace/log": evalTraceLog,
  "integration/http": evalIntegrationHttp,
  "integration/github-get-repo": evalIntegrationGithubGetRepo,
  "integration/github-create-issue": evalIntegrationGithubCreateIssue,
  "contract/validate-input": evalContractValidateInput,
  "contract/validate-output": evalContractValidateOutput,
  "contract/clamp-output": evalContractClampOutput,
  "contract/violations": evalContractViolations,
  "contract/entity-to-tensor": evalContractEntityToTensor,
  "contract/tensor-to-payload": evalContractTensorToPayload,
  "graph/from-entities": evalGraphFromEntities,
  "graph/from-adjacency": evalGraphFromAdjacency,
  "graph/from-edge-list": evalGraphFromEdgeList,
  "graph/add-self-loops": evalGraphAddSelfLoops,
  "graph/to-undirected": evalGraphToUndirected,
  "graph/subgraph": evalGraphSubgraph,
  "graph/k-hop": evalGraphKHop,
  "graph/node-features": evalGraphNodeFeatures,
  "graph/edge-index": evalGraphEdgeIndex,
  "graph/edge-features": evalGraphEdgeFeatures,
  "graph/num-nodes": evalGraphNumNodes,
  "graph/num-edges": evalGraphNumEdges,
  "graph/degree": evalGraphDegree,
  "graph/batch": evalGraphBatch,
  "data/dataset": evalDataDataset,
  "data/dataloader": evalDataDataloader,
  "data/split": evalDataSplit,
  "data/normalize": evalDataNormalize,
  "data/augment": evalDataAugment,
  "data/tokenize": evalDataTokenize,
  "data/pad": evalDataPad
};
function evaluatedChildren(op, expr, dispatched) {
  if (!dispatched) return expr;
  if (op === "let") {
    const [, bindings, ...body] = expr;
    const values = Array.isArray(bindings) ? bindings.map((pair) => Array.isArray(pair) ? pair[1] : pair) : typeof bindings === "object" && bindings !== null ? Object.values(bindings) : [];
    return [...values.filter((v) => v !== void 0), ...body];
  }
  if (op === "fn" || op === "lambda") return expr.slice(2);
  if (op === "quote") return [];
  return expr;
}
var SExpressionEvaluator = class {
  constructor() {
    /**
     * Tier-up compilation cache, keyed by node IDENTITY: schema trees are
     * long-lived parsed objects, so a WeakMap costs no key serialization and
     * is collected with the schema. First sight of a node interprets it and
     * marks it seen; the second sight compiles it — one-off dynamically built
     * expressions never pay compile cost.
     */
    this.compileCache = /* @__PURE__ */ new WeakMap();
    /** Single bound interpreter handed to operator impls — was allocated per dispatch. */
    this.boundInterpret = (expr, c) => this.interpret(expr, c);
  }
  /**
   * Evaluate an S-expression in the given context.
   * Hot trees promote to compiled closures on second use; everything else
   * runs through the interpreter.
   *
   * @param expr - S-expression to evaluate
   * @param ctx - Evaluation context with bindings and effect handlers
   * @returns Result of evaluation
   */
  evaluate(expr, ctx) {
    if (typeof expr === "object" && expr !== null) {
      const entry = this.compileCache.get(expr);
      if (typeof entry === "function") return entry(ctx);
      if (entry === true) {
        const fn = this.compileNode(expr, /* @__PURE__ */ new Map(), void 0);
        this.compileCache.set(expr, fn);
        return fn(ctx);
      }
      this.compileCache.set(expr, true);
    }
    return this.interpret(expr, ctx);
  }
  /**
   * The tree-walking interpreter of record — first-use path and the
   * compiler's fallback for foreign subtrees.
   */
  interpret(expr, ctx) {
    if (!isSExpr(expr)) {
      if (isBinding(expr)) {
        return resolveBinding(expr, ctx);
      }
      if (this.isPlainObject(expr)) {
        const out = {};
        for (const [k, v] of Object.entries(expr)) {
          out[k] = this.evaluate(v, ctx);
        }
        return out;
      }
      if (Array.isArray(expr)) {
        return expr.map((item) => this.evaluate(item, ctx));
      }
      return expr;
    }
    const op = getOperator(expr);
    const args = getArgs(expr);
    const result = this.dispatchOperator(op, args, ctx);
    if (result === UNKNOWN_OPERATOR) {
      return expr.map((item) => this.evaluate(item, ctx));
    }
    return result;
  }
  /**
   * Compile one tree into composed closures. Every node of the tree is
   * registered in `into` so the impls' `evaluate` callback (childDispatch)
   * resolves in-tree children by identity and falls back to interpretation
   * only for foreign subtrees. Arity is asserted HERE, once — a tree that
   * compiles never re-validates.
   */
  compileNode(expr, into, childDispatch) {
    const dispatch = childDispatch ?? ((e, c) => {
      const f = into.get(e);
      return f !== void 0 ? f(c) : this.interpret(e, c);
    });
    let fn;
    if (!isSExpr(expr)) {
      if (isBinding(expr)) {
        fn = (ctx) => resolveBinding(expr, ctx);
      } else if (this.isPlainObject(expr)) {
        const entries = Object.entries(expr).map(
          ([k, v]) => [k, this.compileNode(v, into, dispatch)]
        );
        fn = (ctx) => {
          const out = {};
          for (const [k, f] of entries) out[k] = f(ctx);
          return out;
        };
      } else if (Array.isArray(expr)) {
        const items = expr.map((item) => this.compileNode(item, into, dispatch));
        fn = (ctx) => items.map((f) => f(ctx));
      } else {
        fn = () => expr;
      }
    } else {
      const op = getOperator(expr);
      const args = getArgs(expr);
      assertNotAmbiguousConcat(op);
      assertOperatorArity(op, args.length);
      const impl = OPERATOR_TABLE[op];
      for (const item of evaluatedChildren(op, expr, impl !== void 0)) {
        if (!into.has(item)) this.compileNode(item, into, dispatch);
      }
      if (impl === void 0) {
        fn = (ctx) => expr.map((item) => {
          const f = into.get(item);
          return f !== void 0 ? f(ctx) : this.interpret(item, ctx);
        });
      } else {
        fn = (ctx) => impl(args, dispatch, ctx);
      }
    }
    into.set(expr, fn);
    return fn;
  }
  isPlainObject(value) {
    return typeof value === "object" && value !== null && !Array.isArray(value) && !(value instanceof Date) && Object.getPrototypeOf(value) === Object.prototype;
  }
  /**
   * Evaluate an S-expression as a guard (returns boolean).
   *
   * @param expr - S-expression guard
   * @param ctx - Evaluation context
   * @returns true if guard passes, false otherwise
   */
  evaluateGuard(expr, ctx) {
    const result = this.evaluate(expr, ctx);
    return Boolean(result);
  }
  /**
   * Execute an effect S-expression.
   *
   * @param expr - Effect S-expression (e.g., ["set", "@entity.x", 10])
   * @param ctx - Evaluation context with effect handlers
   */
  executeEffect(expr, ctx) {
    this.evaluate(expr, ctx);
  }
  /**
   * Execute multiple effects in sequence.
   *
   * @param effects - Array of effect S-expressions
   * @param ctx - Evaluation context with effect handlers
   */
  executeEffects(effects, ctx) {
    for (const effect of effects) {
      this.executeEffect(effect, ctx);
    }
  }
  /**
   * Compile an S-expression to a function for faster repeated evaluation.
   * Same machinery as the automatic tier-up in `evaluate`; explicit callers
   * promote immediately instead of on second use.
   *
   * @param expr - S-expression to compile
   * @returns Function that evaluates the expression given a context
   */
  compile(expr) {
    if (typeof expr === "object" && expr !== null) {
      const entry = this.compileCache.get(expr);
      if (typeof entry === "function") return entry;
      const fn = this.compileNode(expr, /* @__PURE__ */ new Map(), void 0);
      this.compileCache.set(expr, fn);
      return fn;
    }
    return this.compileNode(expr, /* @__PURE__ */ new Map(), void 0);
  }
  /**
   * Clear the compilation cache.
   */
  clearCache() {
    this.compileCache = /* @__PURE__ */ new WeakMap();
  }
  /**
   * Evaluate `expr` recording every node's entry, value and exit by structural
   * path, lambda re-entries (`iter`) and arguments the operator never ran
   * (`skip`). Runs the same operator implementations as `evaluate`, never the
   * compiled closures. Never throws: a failure is returned with the trace so far.
   */
  evaluateTraced(expr, ctx) {
    const trace = [];
    const paths = /* @__PURE__ */ new Map();
    const indexPaths = (node, path) => {
      if (typeof node !== "object" || node === null) return;
      paths.set(node, path);
      if (Array.isArray(node)) node.forEach((child, i) => indexPaths(child, [...path, i]));
      else for (const [k, child] of Object.entries(node)) indexPaths(child, [...path, k]);
    };
    indexPaths(expr, []);
    const entered = /* @__PURE__ */ new Set();
    const keyOf = (path) => path.join("\0");
    const ranUnder = (path) => {
      const k = keyOf(path);
      for (const e of entered) if (e === k || e.startsWith(k + "\0")) return true;
      return false;
    };
    const visit = (node, path, c) => {
      const k = keyOf(path);
      if (entered.has(k)) trace.push({ kind: "iter", path });
      entered.add(k);
      trace.push({ kind: "enter", path });
      try {
        const value = step(node, path, c);
        trace.push({ kind: "exit", path, value });
        return value;
      } catch (err) {
        trace.push({ kind: "exit", path, error: err instanceof Error ? err.message : String(err) });
        throw err;
      }
    };
    const step = (node, path, c) => {
      if (!isSExpr(node)) {
        if (isBinding(node)) return resolveBinding(node, c);
        if (this.isPlainObject(node)) {
          const out = {};
          for (const [k, v] of Object.entries(node)) out[k] = visit(v, [...path, k], c);
          return out;
        }
        if (Array.isArray(node)) return node.map((item, i) => visit(item, [...path, i], c));
        return node;
      }
      const op = getOperator(node);
      const args = getArgs(node);
      const consumed = /* @__PURE__ */ new Set();
      const childPath = (child) => {
        if (typeof child === "object" && child !== null) return paths.get(child) ?? null;
        let first = -1;
        for (let i = 0; i < args.length; i++) {
          if (args[i] !== child) continue;
          if (first < 0) first = i;
          if (!consumed.has(i)) {
            consumed.add(i);
            return [...path, i + 1];
          }
        }
        return first < 0 ? null : [...path, first + 1];
      };
      const tracedChild = (child, c2) => {
        const p2 = childPath(child);
        return p2 === null ? this.evaluate(child, c2) : visit(child, p2, c2);
      };
      const result = this.dispatchOperator(op, args, c, tracedChild);
      const value = result === UNKNOWN_OPERATOR ? node.map((item, i) => visit(item, [...path, i], c)) : result;
      args.forEach((_, i) => {
        const p2 = [...path, i + 1];
        if (!ranUnder(p2)) trace.push({ kind: "skip", path: p2 });
      });
      return value;
    };
    try {
      return { value: visit(expr, [], ctx), trace };
    } catch (err) {
      return { trace, error: err instanceof Error ? err.message : String(err) };
    }
  }
  /**
   * Dispatch to the appropriate operator implementation.
   */
  /**
   * Dispatch to the appropriate operator implementation.
   */
  dispatchOperator(op, args, ctx, evaluate2 = this.boundInterpret) {
    assertNotAmbiguousConcat(op);
    assertOperatorArity(op, args.length);
    const impl = OPERATOR_TABLE[op];
    if (impl === void 0) return UNKNOWN_OPERATOR;
    return impl(args, evaluate2, ctx);
  }
};
var evaluator = new SExpressionEvaluator();
function evaluate(expr, ctx) {
  return evaluator.evaluate(expr, ctx);
}
function evaluateTraced(expr, ctx) {
  return evaluator.evaluateTraced(expr, ctx);
}
function evaluateGuard(expr, ctx) {
  return evaluator.evaluateGuard(expr, ctx);
}
function executeEffect(expr, ctx) {
  evaluator.executeEffect(expr, ctx);
}
function executeEffects(effects, ctx) {
  evaluator.executeEffects(effects, ctx);
}

// listen-payload.ts
var evaluateListenPayloadExpr = (expr, payload) => evaluate(expr, createMinimalContext({}, payload));

export { EvalTypeMismatchError, SExpressionEvaluator, createChildContext, createEffectContext, createMinimalContext, evalAbs, evalAdd, evalAnd, evalAtomic, evalCallService, evalCeil, evalClamp, evalConcat, evalCount, evalDecrement, evalDeref, evalDespawn, evalDivide, evalDo, evalEmit, evalEmpty, evalEqual, evalFilter, evalFind, evalFirst, evalFloor, evalFn, evalGreaterThan, evalGreaterThanOrEqual, evalIf, evalIncludes, evalIncrement, evalLast, evalLessThan, evalLessThanOrEqual, evalLet, evalList, evalMap, evalMatches, evalMax, evalMin, evalModulo, evalMultiply, evalNavigate, evalNot, evalNotEqual, evalNotify, evalNth, evalOr, evalPersist, evalRef, evalRenderUI, evalRound, evalSet, evalSetDynamic, evalSpawn, evalSubtract, evalSum, evalSwap, evalWatch, evalWhen, evaluate, evaluateGuard, evaluateListenPayloadExpr, evaluateTraced, evaluator, executeEffect, executeEffects, lambdaSourceOf, resolveBinding, runtimeTypeName };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map